import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import {
  ArrowLeft, Trash2, UserPlus, UserMinus,
  CheckCircle, AlertCircle, Pencil, X, LogOut,
  ZoomIn, ZoomOut, Maximize2, Undo2, Redo2, Download, Copy, Mail,
  DoorOpen, Flame, Droplets, Zap, Eraser, Square,
  LayoutGrid, Circle, RectangleHorizontal, Columns3, Hand, FireExtinguisher,
  MoveRight, Building2, Search,
  Ruler, Type, Minus, Image as ImageIcon,
} from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAND_COLORS = [
  '#3b82f6','#10b981','#f59e0b','#8b5cf6',
  '#06b6d4','#f97316','#84cc16','#ec4899',
  '#6366f1','#14b8a6','#ef4444','#a855f7'
];

const OFFSET = 0;
const BASE_CELL = 18;

/* ─── Infrastructure tool config ──────────────────────────────────────────── */
const INFRA_TYPES = {
  corridor:      { label: 'Koridor',         labelEn: 'Corridor',       fill: 'rgba(148,163,184,0.42)', border: '#94a3b8', icon: '╱╲' },
  door:          { label: 'Kapı',            labelEn: 'Door',           fill: 'transparent',            border: '#1f2937', icon: 'D'  },
  fire_cabinet:  { label: 'Yangın Dolabı',   labelEn: 'Fire Cabinet',   fill: 'rgba(239,68,68,0.35)',   border: '#dc2626', icon: 'YD' },
  water:         { label: 'Su',              labelEn: 'Water',          fill: 'rgba(59,130,246,0.35)',  border: '#2563eb', icon: '◎'  },
  electricity:   { label: 'Elektrik',        labelEn: 'Electricity',    fill: 'rgba(234,179,8,0.35)',   border: '#ca8a04', icon: '⚡' },
};

const TOOL_BUTTONS = [
  { id: 'stand',         label: 'Stand Çiz',    labelEn: 'Draw Stand',    color: '#7c3aed', Icon: Square           },
  { id: 'corridor',      label: 'Koridor',       labelEn: 'Corridor',      color: '#6b7280', Icon: LayoutGrid       },
  { id: 'door',          label: 'Kapı',          labelEn: 'Door',          color: '#1f2937', Icon: DoorOpen         },
  { id: 'fire_cabinet',  label: 'Yangın Dolabı', labelEn: 'Fire Cabinet',  color: '#dc2626', Icon: FireExtinguisher },
  { id: 'water',         label: 'Su',            labelEn: 'Water',         color: '#2563eb', Icon: Droplets         },
  { id: 'electricity',   label: 'Elektrik',      labelEn: 'Electricity',   color: '#ca8a04', Icon: Zap              },
  { id: 'eraser',        label: 'Silgi',         labelEn: 'Eraser',        color: '#94a3b8', Icon: Eraser           },
];

// Annotation overlay tools (measure / shapes / text). Logo is a button, not a draw tool.
const ANNOTATION_TOOLS = [
  { id: 'measure', label: 'Ölçü (DIM)', labelEn: 'Measure',  color: '#0891b2', Icon: Ruler  },
  { id: 'ann_line',  label: 'Çizgi',    labelEn: 'Line',     color: '#0f172a', Icon: Minus   },
  { id: 'ann_rect',  label: 'Kare',     labelEn: 'Square',   color: '#0f172a', Icon: Square  },
  { id: 'ann_circle',label: 'Daire',    labelEn: 'Circle',   color: '#0f172a', Icon: Circle  },
  { id: 'ann_text',  label: 'Yazı',     labelEn: 'Text',     color: '#0f172a', Icon: Type    },
];
const ANN_DRAW_TOOLS = ['measure', 'ann_line', 'ann_rect', 'ann_circle'];
const FONT_FAMILIES = ['Inter', 'Arial', 'Georgia', 'Courier New', 'Playfair Display', 'Times New Roman'];

const DEFAULT_SETTINGS = {
  available_color: '#22c55e',
  reserved_color: '#f59e0b',
  unavailable_color: '#ef4444',
  transparency: 70,
};

const CURRENCIES = ['USD', 'EUR', 'TRY', 'GBP'];

const cellKey = (col, row) => `${col},${row}`;

function hexToRgba(hex, alpha) {
  if (!hex || hex.length < 7) return `rgba(148,163,184,${alpha})`;
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/* ── Country flag helper ──────────────────────────────────────────────────── */
const COUNTRY_ISO = {
  // English
  'turkey':'TR','germany':'DE','france':'FR','united kingdom':'GB','uk':'GB',
  'united states':'US','usa':'US','china':'CN','japan':'JP','russia':'RU',
  'brazil':'BR','india':'IN','australia':'AU','canada':'CA','italy':'IT',
  'spain':'ES','netherlands':'NL','sweden':'SE','norway':'NO','denmark':'DK',
  'finland':'FI','poland':'PL','ukraine':'UA','switzerland':'CH','austria':'AT',
  'belgium':'BE','portugal':'PT','greece':'GR','czechia':'CZ','czech republic':'CZ',
  'hungary':'HU','romania':'RO','south korea':'KR','indonesia':'ID','malaysia':'MY',
  'thailand':'TH','vietnam':'VN','singapore':'SG','saudi arabia':'SA','iran':'IR',
  'iraq':'IQ','israel':'IL','jordan':'JO','egypt':'EG','south africa':'ZA',
  'nigeria':'NG','kenya':'KE','mexico':'MX','argentina':'AR','colombia':'CO',
  'chile':'CL','peru':'PE','pakistan':'PK','bangladesh':'BD','azerbaijan':'AZ',
  'georgia':'GE','armenia':'AM','kazakhstan':'KZ','uzbekistan':'UZ','qatar':'QA',
  'uae':'AE','united arab emirates':'AE','kuwait':'KW','bahrain':'BH','oman':'OM',
  'lebanon':'LB','syria':'SY','new zealand':'NZ','serbia':'RS','croatia':'HR',
  'ukraine':'UA','bulgaria':'BG','slovakia':'SK','slovenia':'SI','estonia':'EE',
  'latvia':'LV','lithuania':'LT','moldova':'MD','belarus':'BY','albania':'AL',
  'north macedonia':'MK','bosnia':'BA','montenegro':'ME','kosovo':'XK',
  'afghanistan':'AF','algeria':'DZ','morocco':'MA','tunisia':'TN','libya':'LY',
  'ethiopia':'ET','ghana':'GH','tanzania':'TZ','uganda':'UG','rwanda':'RW',
  'senegal':'SN','ivory coast':'CI','cameroon':'CM','sudan':'SD','angola':'AO',
  'mozambique':'MZ','zambia':'ZM','zimbabwe':'ZW','botswana':'BW','namibia':'NA',
  'philippines':'PH','myanmar':'MM','cambodia':'KH','laos':'LA','sri lanka':'LK',
  'nepal':'NP','mongolia':'MN','north korea':'KP','taiwan':'TW','hong kong':'HK',
  'venezuela':'VE','ecuador':'EC','bolivia':'BO','uruguay':'UY','paraguay':'PY',
  'panama':'PA','costa rica':'CR','guatemala':'GT','cuba':'CU','dominican republic':'DO',
  'iceland':'IS','ireland':'IE','luxembourg':'LU','malta':'MT','cyprus':'CY',
  'yemen':'YE','tajikistan':'TJ','turkmenistan':'TM','kyrgyzstan':'KG',
  // Turkish
  'türkiye':'TR','almanya':'DE','fransa':'FR','ingiltere':'GB','birleşik krallık':'GB',
  'abd':'US','çin':'CN','japonya':'JP','rusya':'RU','brezilya':'BR','hindistan':'IN',
  'avustralya':'AU','kanada':'CA','italya':'IT','ispanya':'ES','hollanda':'NL',
  'isveç':'SE','norveç':'NO','danimarka':'DK','finlandiya':'FI','polonya':'PL',
  'ukrayna':'UA','isviçre':'CH','avusturya':'AT','belçika':'BE','portekiz':'PT',
  'yunanistan':'GR','güney kore':'KR','endonezya':'ID','malezya':'MY','tayland':'TH',
  'singapur':'SG','suudi arabistan':'SA','israil':'IL','ürdün':'JO','mısır':'EG',
  'güney afrika':'ZA','nijerya':'NG','meksika':'MX','arjantin':'AR','azerbaycan':'AZ',
  'gürcistan':'GE','ermenistan':'AM','kazakistan':'KZ','özbekistan':'UZ','katar':'QA',
  'birleşik arap emirlikleri':'AE','bae':'AE','kuveyt':'KW','bahreyn':'BH',
  'umman':'OM','lübnan':'LB','suriye':'SY','yeni zelanda':'NZ','sırbistan':'RS',
  'hırvatistan':'HR','bulgaristan':'BG','slovakya':'SK','slovenya':'SI','estonya':'EE',
  'letonya':'LV','litvanya':'LT','moldova':'MD','belarus':'BY','arnavutluk':'AL',
  'afganistan':'AF','cezayir':'DZ','fas':'MA','tunus':'TN','libya':'LY',
  'etiyopya':'ET','gana':'GH','tanzanya':'TZ','uganda':'UG','ruanda':'RW',
  'senegal':'SN','fildişi sahili':'CI','kamerun':'CM','sudan':'SD','angola':'AO',
  'mozambik':'MZ','zambiya':'ZM','zimbabve':'ZW','botsvana':'BW','namibya':'NA',
  'filipinler':'PH','myanmar':'MM','kamboçya':'KH','laos':'LA','sri lanka':'LK',
  'nepal':'NP','moğolistan':'MN','kuzey kore':'KP','tayvan':'TW','hong kong':'HK',
  'venezuela':'VE','ekvador':'EC','bolivya':'BO','uruguay':'UY','paraguay':'PY',
  'panama':'PA','kosta rika':'CR','guatemala':'GT','küba':'CU','dominik cumhuriyeti':'DO',
  'izlanda':'IS','irlanda':'IE','lüksemburg':'LU','malta':'MT','kıbrıs':'CY',
  'yemen':'YE','tacikistan':'TJ','türkmenistan':'TM','kırgızistan':'KG',
};
const countryToFlag = (country) => {
  if (!country) return '';
  const iso = COUNTRY_ISO[country.toLowerCase().trim()];
  if (!iso) return '';
  return Array.from(iso).map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)).join('');
};

/* ── Text wrap helper (SVG coordinate units) ─────────────────────────────── */
const wrapText = (text) => {
  // Kelime bazlı sarma: HER KELİME KENDİ SATIRINDA. Kelimeleri ASLA harf harf
  // bölmeyiz — sığmayan tek kelime için font küçültülür (shrink döngüsü halleder).
  // "KOÇ GRUBU" → ["KOÇ","GRUBU"], "SIEMENS" → ["SIEMENS"], "REFRESHMENT AREA" → ["REFRESHMENT","AREA"].
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 5);
};

const emptyModal = () => ({
  stand_no: '',
  stand_name: '',
  status: 'available',
  color: STAND_COLORS[Math.floor(Math.random() * STAND_COLORS.length)],
  transparency: 70,
  show_area: true,
  price_per_sqm: '',
  currency: 'USD',
  ceiling_height: '',
  max_stand_height: '',
  hide_status: false,
  hide_company: false,
  open_sides: ['north', 'south', 'east', 'west'],
  stand_type: '',
  assigned_modular_package_id: '',
});

/* ════════════════════════════════════════════════════════════════════════════ */
const HallEditorPage = () => {
  const { hallId } = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const { language, t } = useLanguage();
  const tr = language === 'tr';
  // Araç etiketi: dile göre
  const tl = (tool) => language === 'en' ? (tool.labelEn || tool.label) : tool.label;
  // Token her API çağrısında taze okunur (backend restart'ta stale closure sorununu önler)
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('cop31_ultra_token')}` });

  const [hall, setHall]         = useState(null);
  const [stands, setStands]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [gridSettings, setGridSettings] = useState(DEFAULT_SETTINGS);

  /* ── Zoom ──────────────────────────────────────────────────────────────── */
  const [zoom, setZoom] = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const MIN_ZOOM = 0.08;
  const MAX_ZOOM = 6.0;
  const containerRef = useRef(null);
  const svgRef = useRef(null);

  /* ── Pan (scroll) state ───────────────────────────────────────────────── */
  const panRef = useRef(null); // {startX, startY, scrollLeft, scrollTop}

  /* ── Active tool ──────────────────────────────────────────────────────── */
  const [activeTool, setActiveTool] = useState('stand');

  /* ── Annotations layer (measure / shapes / text / logo) ────────────────── */
  const [annotations, setAnnotations] = useState([]);
  const [selectedAnnId, setSelectedAnnId] = useState(null);
  const [annDraft, setAnnDraft] = useState(null);          // live draft while drawing
  const [annStyle, setAnnStyle] = useState({ color: '#dc2626', fontSize: 16, fontFamily: 'Inter' });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const bgInputRef = useRef(null);
  const annRef = useRef([]);
  const annDraftRef = useRef(null);
  const annDragRef = useRef(null);                          // {mode, id, start:{x,y}, orig}
  const logoInputRef = useRef(null);
  useEffect(() => { annRef.current = annotations; }, [annotations]);
  useEffect(() => { annDraftRef.current = annDraft; }, [annDraft]);

  /* ── Rectangle drag (stand drawing) ──────────────────────────────────── */
  const [dragStart, setDragStart] = useState(null);   // {col, row}
  const [dragEnd,   setDragEnd]   = useState(null);   // {col, row}
  const [isDragging, setIsDragging] = useState(false);

  /* ── Stand modal (create + edit) ──────────────────────────────────────── */
  const [showModal,    setShowModal]    = useState(false);
  const [modalData,    setModalData]    = useState(emptyModal());
  const [pendingCells, setPendingCells] = useState([]);  // cells for new stand
  const [editingStand, setEditingStand] = useState(null); // stand being edited
  const [savingStand,  setSavingStand]  = useState(false);

  /* ── Selected stand ───────────────────────────────────────────────────── */
  const [selectedStand, setSelectedStand] = useState(null);

  /* ── Cross-hall move modal ───────────────────────────────────────────────
      Lets the operator move an assigned tenant from the currently-open hall
      to ANY other hall's available stand without leaving this editor. The
      backend `assign_stand` endpoint already frees the source atomically
      when it sees a previous assignment for the same tenant.
   */
  const [crossMoveOpen, setCrossMoveOpen]       = useState(false);
  const [crossMoveLoading, setCrossMoveLoading] = useState(false);
  const [crossMoveHalls, setCrossMoveHalls]     = useState([]);  // [{hall_id, hall_name, zone, stands:[{id, stand_no, area, ...}]}]
  const [crossMoveSearch, setCrossMoveSearch]   = useState('');
  const [crossMovePending, setCrossMovePending] = useState(null); // confirm modal payload

  const openMoveAcrossHalls = async () => {
    if (!selectedStand?.tenant_id) return;
    setCrossMoveOpen(true);
    setCrossMoveLoading(true);
    try {
      const r = await apiClient.get('/ultra-admin/halls/available-stands');
      // Hide the current hall from the picker — moving "within the same hall"
      // already has its own flow (drag-and-drop / Katılımcı Ata over a free
      // stand). This modal is strictly about cross-hall routing.
      setCrossMoveHalls((r.data || []).filter(h => h.hall_id !== hallId));
    } catch (e) {
      console.error(e);
      setCrossMoveHalls([]);
    } finally {
      setCrossMoveLoading(false);
    }
  };

  // Fire the actual move via the existing assign endpoint on the destination.
  const doCrossHallMove = async () => {
    if (!crossMovePending || !selectedStand?.tenant_id) return;
    const { dstHallId, dstStandId } = crossMovePending;
    setAssigning(true);
    try {
      await apiClient.post(
        `/ultra-admin/halls/${dstHallId}/stands/${dstStandId}/assign`,
        { tenant_id: selectedStand.tenant_id },
      );
      setCrossMovePending(null);
      setCrossMoveOpen(false);
      // Reload current hall — the source stand is now free.
      const fresh = await fetchHall();
      setSelectedStand(null);
      // Optional: redirect operator to the destination hall to see the new placement.
      if (typeof navigate === 'function') {
        navigate(`/ultra-admin/halls/${dstHallId}`);
      }
      void fresh;
    } catch (e) {
      console.error(e);
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || (tr ? 'Taşıma başarısız.' : 'Move failed.'));
    } finally {
      setAssigning(false);
    }
  };

  /* ── Current admin identity (for stand ownership rules) ───────────────── */
  // Loaded once from /api/auth/me. Used to:
  //   • disable Düzenle / Sil for stands created by another admin,
  //   • disable Atamayı Kaldır / Taşı for assignments owned by another admin,
  //   • show a confirm modal when a Super Admin overrides another admin.
  const [currentAdmin, setCurrentAdmin] = useState(null);
  /* ── Super admin override confirm ─────────────────────────────────────── */
  // { ownerName, action: 'unassign'|'update'|'delete'|'assign', onConfirm }
  const [overrideConfirm, setOverrideConfirm] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiClient.get('/auth/me').then(r => {
      if (!cancelled) setCurrentAdmin(r.data || null);
    }).catch(() => { /* token will be cleaned up by interceptor */ });
    return () => { cancelled = true; };
  }, []);

  /* ── Ownership helpers ────────────────────────────────────────────────── */
  // Mirror of backend `_check_stand_ownership` so the UI agrees with the
  // 403 the API would return. Returns { allowed, ownerName, ownerKind } where
  // ownerKind ∈ 'creator' | 'assigner' | '' and allowed=true when the active
  // admin owns the stand (or is Super Admin).
  const ownershipFor = useCallback((stand, action) => {
    if (!stand) return { allowed: true, ownerName: '', ownerKind: '' };
    if (!currentAdmin) return { allowed: true, ownerName: '', ownerKind: '' }; // not loaded yet
    if (currentAdmin.is_super_admin) {
      // Super admin can do anything but UI marks "another admin's" assignments
      // so we can show the override modal.
      const aId = stand.assigned_by_admin_id || '';
      const cId = stand.created_by_admin_id  || '';
      const me  = currentAdmin.id || '';
      const isOther =
        (action === 'unassign' || action === 'assign')
          ? (stand.tenant_id && aId && aId !== me)
          : (cId && cId !== me) || (stand.tenant_id && aId && aId !== me);
      const ownerName = action === 'unassign' || action === 'assign'
        ? (stand.assigned_by_admin_name || '')
        : (stand.created_by_admin_name || stand.assigned_by_admin_name || '');
      return { allowed: true, ownerName: isOther ? ownerName : '', ownerKind: isOther ? (action === 'unassign' || action === 'assign' ? 'assigner' : 'creator') : '' };
    }
    const me = currentAdmin.id || '';
    if (action === 'assign' || action === 'unassign' || action === 'move') {
      const aId = stand.assigned_by_admin_id || '';
      if (stand.tenant_id && aId && aId !== me) {
        return { allowed: false, ownerName: stand.assigned_by_admin_name || '?', ownerKind: 'assigner' };
      }
      return { allowed: true, ownerName: '', ownerKind: '' };
    }
    if (action === 'update' || action === 'delete') {
      const cId = stand.created_by_admin_id  || '';
      const aId = stand.assigned_by_admin_id || '';
      if (cId && cId !== me) return { allowed: false, ownerName: stand.created_by_admin_name || '?', ownerKind: 'creator' };
      if (stand.tenant_id && aId && aId !== me) return { allowed: false, ownerName: stand.assigned_by_admin_name || '?', ownerKind: 'assigner' };
      return { allowed: true, ownerName: '', ownerKind: '' };
    }
    return { allowed: true, ownerName: '', ownerKind: '' };
  }, [currentAdmin]);

  // Wrap an action so that:
  //   • Non-owner / non-super-admin → toast 403 message and abort.
  //   • Super Admin overriding another admin → confirm modal first.
  //   • Owner → run immediately.
  const guardedRun = useCallback((stand, action, runner, labelTr, labelEn) => {
    const { allowed, ownerName } = ownershipFor(stand, action);
    if (!allowed) {
      const msg = labelEn && (currentAdmin?.zone === 'en')
        ? `Only ${ownerName} or a Super Admin can ${labelEn} this stand.`
        : `Bu işlemi sadece ${ownerName} veya Super Admin yapabilir.`;
      // eslint-disable-next-line no-alert
      alert(msg);
      return;
    }
    if (currentAdmin?.is_super_admin && ownerName) {
      setOverrideConfirm({
        action,
        ownerName,
        actionLabel: labelTr || action,
        onConfirm: () => { setOverrideConfirm(null); runner(); },
      });
      return;
    }
    runner();
  }, [ownershipFor, currentAdmin]);

  /* ── Move / Resize interaction ─────────────────────────────────────────── */
  const [interactMode, setInteractMode]   = useState(null);   // null | 'move' | 'resize'
  const [interactStart, setInteractStart] = useState(null);   // {cell, originalCells, originalBounds, handle?}
  const [previewCells, setPreviewCells]   = useState(null);   // live cells preview during drag

  /* ── Email stand dialog ────────────────────────────────────────────────── */
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailMsg,        setEmailMsg]        = useState('');
  const [sendingEmail,    setSendingEmail]    = useState(false);

  /* ── Unassign confirmation ─────────────────────────────────────────────── */
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const [unassignSource,      setUnassignSource]      = useState(null); // 'panel' | 'modal'

  /* ── Assignment ───────────────────────────────────────────────────────── */
  const [showAssignModal,      setShowAssignModal]      = useState(false);
  const [moveConfirm,          setMoveConfirm]          = useState(null);  // null | { tenantId, tenantName, fromStand, fromHall, toStand, toHall }
  const [tenants,              setTenants]              = useState([]);
  const [assigning,            setAssigning]            = useState(false);
  const [tenantSearch,         setTenantSearch]         = useState('');
  // Phase 86AU — Green Zone stand-type tagging at assign time.
  // assignStandType ∈ 'modular' | 'space_only'. Default to space_only so the
  // admin makes an explicit choice for Modular (which has the area gate).
  const [assignStandType,      setAssignStandType]      = useState('space_only');
  const [modularAreas,         setModularAreas]         = useState([25, 50, 100, 150]);
  // Phase 86AZ.2 — When admin picks Modular at assign time, they can also
  // bind a specific Modular Stand Package. Customer then sees ONLY this
  // package on their dashboard (no catalog browsing).
  const [modularPackages,      setModularPackages]      = useState([]);
  const [assignModularPkgId,   setAssignModularPkgId]   = useState('');

  // Fetch the configured Modular Stand Area Options once. Drives the
  // "Modular" radio's disabled state in the Assign modal.
  useEffect(() => {
    apiClient.get('/ultra-admin/stand-options')
      .then(r => { if (Array.isArray(r.data?.modular_areas)) setModularAreas(r.data.modular_areas); })
      .catch(() => {});
    // Phase 86AZ.2 — Pre-fetch all active modular packages so the assign /
    // edit modal can filter by stand area without an extra round-trip.
    apiClient.get('/ultra-admin/modular-packages')
      .then(r => { if (Array.isArray(r.data)) setModularPackages(r.data); })
      .catch(() => {});
  }, []);

  /* ── Modal tenant picker ───────────────────────────────────────────────── */
  const [modalTenantId,        setModalTenantId]        = useState(null);
  const [modalTenantSearch,    setModalTenantSearch]    = useState('');
  const [showModalTenantDrop,  setShowModalTenantDrop]  = useState(false);
  // Phase 86AU.2 — Close-on-outside-click + ESC for the tenant dropdown so
  // it behaves like every other modern combobox. Without this admins had to
  // pick a row to dismiss the panel (UX complaint reported).
  const tenantPickerRef = useRef(null);
  useEffect(() => {
    if (!showModalTenantDrop) return;
    const onPointerDown = (e) => {
      if (tenantPickerRef.current && !tenantPickerRef.current.contains(e.target)) {
        setShowModalTenantDrop(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowModalTenantDrop(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown',   onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown',   onKey);
    };
  }, [showModalTenantDrop]);

  /* ── Infrastructure ───────────────────────────────────────────────────── */
  const infraRef = useRef(new Map());
  const [infraItems, _setInfraItems] = useState(new Map());
  const setInfraItems = (updater) => {
    _setInfraItems(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      infraRef.current = next;
      return next;
    });
  };
  const histSnapshots = useRef([]);
  const histIdxRef    = useRef(-1);
  const [histState, setHistState] = useState({ canUndo: false, canRedo: false });

  /* ── Columns ──────────────────────────────────────────────────────────── */
  const [columns, setColumns]           = useState([]);
  const [columnModal, setColumnModal]   = useState(false);
  const [colForm, setColForm]           = useState({ shape: 'kare', w: 1, h: 1, d: 1 });
  const [draggingCol, setDraggingCol]   = useState(null); // {id, offX, offY}
  const draggingColRef                  = useRef(null);
  const [selectedColId, setSelectedColId] = useState(null); // seçili kolon id

  /* ── Seçili altyapı öğesi (su/elektrik/yangın dolabı) ─────────────────── */
  const [selectedInfraId, setSelectedInfraId] = useState(null);

  const pushHistory = useCallback((map) => {
    const snaps = histSnapshots.current.slice(0, histIdxRef.current + 1);
    snaps.push(new Map(map));
    histSnapshots.current = snaps;
    histIdxRef.current = snaps.length - 1;
    setHistState({ canUndo: histIdxRef.current > 0, canRedo: false });
  }, []);

  /* ── Fetch ────────────────────────────────────────────────────────────── */
  const fetchHall = useCallback(async () => {
    try {
      const [hallRes, settingsRes] = await Promise.all([
        apiClient.get(`/ultra-admin/halls/${hallId}`),
        apiClient.get(`/ultra-admin/grid-settings`).catch(() => ({ data: DEFAULT_SETTINGS }))
      ]);
      setHall(hallRes.data);
      setStands(hallRes.data.stands || []);
      setGridSettings({ ...DEFAULT_SETTINGS, ...settingsRes.data });
      const infra = hallRes.data.infra_items || [];
      const map = new Map();
      // Yeni dikdörtgen formatı: {id, type, col, row, width, height}
      // Eski format (width/height yok) → görmezden gel (görsel bozukluğa yol açar)
      infra.forEach(it => {
        if (!it.id || !it.width || !it.height) return; // eski format → atla
        map.set(it.id, { id: it.id, type: it.type, col: it.col, row: it.row, width: it.width, height: it.height });
      });
      setInfraItems(map);
      histSnapshots.current = [new Map(map)];
      histIdxRef.current = 0;
      setHistState({ canUndo: false, canRedo: false });
      setColumns(hallRes.data.columns || []);
      setAnnotations(hallRes.data.annotations || []);
      setLoading(false);
      return hallRes.data; // return for callers that need fresh data
    } catch (e) {
      console.error('[fetchHall]', e);
      setLoading(false);
      // Token geçersiz/süresi dolmuş → login'e yönlendir
      if (e.response?.status === 401) {
        localStorage.removeItem('cop31_ultra_token');
        localStorage.removeItem('cop31_ultra_user');
        navigate('/giris?tab=ultra');
      }
    }
  }, [hallId, navigate]); // eslint-disable-line

  useEffect(() => {
    fetchHall();
    // Not: visibilitychange/focus kaldırıldı — aktif çizim sırasında yeniden render sorununa
    // yol açıyordu. Token hatası için global interceptor → /giris yönlendirmesi yeterli.
  }, [fetchHall]);

  /* ── Deep-link auto-focus: ?focus_stand=<id> ───────────────────────────── */
  // When the editor is opened via "View on Grid" from Stand Requests, scroll
  // the grid so the assigned stand is centered, zoom in, and select it so the
  // right panel shows its details.
  useEffect(() => {
    const focusId = searchParams.get('focus_stand');
    if (!focusId || !stands || stands.length === 0) return;
    const target = stands.find(s => s.id === focusId);
    if (!target) return;
    setSelectedStand(target);
    setActiveTool('stand');
    setZoom(1.6);  // zoom in for clarity
    // Smooth-scroll the grid so the stand is centered (approximation; cell
    // positions are screen-pixel-based so this lands "near" the target).
    setTimeout(() => {
      const el = containerRef.current;
      if (!el || !target.cells || !target.cells.length) return;
      const cs = (hall?.cell_size || 10) * 1.6;
      const minCol = Math.min(...target.cells.map(c => c.col));
      const minRow = Math.min(...target.cells.map(c => c.row));
      const maxCol = Math.max(...target.cells.map(c => c.col));
      const maxRow = Math.max(...target.cells.map(c => c.row));
      const cx = ((minCol + maxCol) / 2) * cs;
      const cy = ((minRow + maxRow) / 2) * cs;
      el.scrollTo({
        left: Math.max(0, cx - el.clientWidth / 2),
        top:  Math.max(0, cy - el.clientHeight / 2),
        behavior: 'smooth',
      });
    }, 250);
  }, [stands, searchParams, hall]);

  /* ── Zoom wheel ───────────────────────────────────────────────────────── */

  useEffect(() => {
    // Window-level wheel listener — most reliable for canvas editors.
    // Fires before the browser scroll, prevents default if mouse is over our container.
    const handler = (e) => {
      const el = containerRef.current;
      if (!el || !el.contains(e.target)) return;
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.12 : -0.12;
      setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, +(prev + delta).toFixed(2))));
    };
    window.addEventListener('wheel', handler, { passive: false });
    return () => window.removeEventListener('wheel', handler);
  }, []); // setZoom is stable — no deps needed

  /* ── Derived sizes ────────────────────────────────────────────────────── */
  const cellSize = hall
    ? Math.max(4, Math.min(BASE_CELL, Math.floor(
        Math.min((780 - OFFSET) / hall.width, (540 - OFFSET) / hall.height)
      )))
    : BASE_CELL;

  const baseSvgW = (hall?.width  || 0) * cellSize + 2;
  const baseSvgH = (hall?.height || 0) * cellSize + 2;

  /* ── Grid unit (metres per cell) — supports 0.5 m halls ───────────────── */
  const gridUnit    = hall?.grid_unit || 1;
  const cellsToM    = (n) => { const v = (n || 0) * gridUnit; return Number.isInteger(v) ? v : +v.toFixed(1); };
  const cellsToArea = (n) => { const v = (n || 0) * gridUnit * gridUnit; return Number.isInteger(v) ? v : +v.toFixed(2); };
  const mToCells    = (m) => Math.max(1, Math.round((parseFloat(m) || 0) / gridUnit));

  /* ── Dynamic visual params (FIXED: constant visual width) ────────────── */
  const strokeW       = 0.5  / zoom;
  const borderStrokeW = 1.5  / zoom;
  // Font scales naturally with zoom (bigger when zoomed in, smaller when zoomed out)
  const standFontSz   = cellSize * 0.55;
  const showGrid      = zoom > 0.04;

  /* ── Stand color helpers ──────────────────────────────────────────────── */
  const alpha = (modalData.transparency || gridSettings.transparency) / 100;

  // Effective status: if tenant assigned, minimum is 'reserved' (never shows as available)
  const effectiveStatus = (stand) => {
    if (stand.tenant_id) return stand.status === 'unavailable' ? 'unavailable' : 'reserved';
    return stand.status || 'available';
  };

  // ── Stand colour rules (Phase 74d — 2026-04-27) ──────────────────────────
  // Strict three-state palette. Custom `stand.color` per-stand overrides
  // were producing pink/turquoise/etc that looked random to the operator
  // and made "available vs assigned" hard to read at a glance. Removed.
  //   • available   → green  (gridSettings.available_color)
  //   • reserved    → orange (gridSettings.reserved_color)   ← also covers tenant-assigned
  //   • unavailable → red    (gridSettings.unavailable_color) ← "Sold"
  const getStandFill = (stand) => {
    const a = (stand.transparency || gridSettings.transparency) / 100;
    const s = effectiveStatus(stand);
    if (s === 'reserved')    return hexToRgba(gridSettings.reserved_color, a);
    if (s === 'unavailable') return hexToRgba(gridSettings.unavailable_color, a);
    return hexToRgba(gridSettings.available_color, a);
  };

  const getStandBorder = (stand) => {
    const s = effectiveStatus(stand);
    if (s === 'reserved')    return gridSettings.reserved_color;
    if (s === 'unavailable') return gridSettings.unavailable_color;
    return gridSettings.available_color;
  };

  /* ── Occupied map ─────────────────────────────────────────────────────── */
  const occupiedMap = React.useMemo(() => {
    const map = {};
    stands.forEach(s => (s.cells || []).forEach(c => { map[cellKey(c.col, c.row)] = s; }));
    return map;
  }, [stands]);

  /* ── SVG cell from mouse event ────────────────────────────────────────── */
  const svgToCell = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top)  / zoom;
    const col = Math.floor(x / cellSize);
    const row = Math.floor(y / cellSize);
    if (!hall || col < 0 || row < 0 || col >= hall.width || row >= hall.height) return null;
    return { col, row };
  };

  // Stand çizimi için EN YAKIN grid köşesine yapışma (köşe-mıknatısı).
  // Hücre yerine köşe (0..width / 0..height) döndürür; imlece en yakın grid
  // kesişimine yuvarlanır → standların kenarları grid köşelerine hizalanır.
  const svgToCorner = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || !hall) return null;
    const x = (e.clientX - rect.left) / zoom / cellSize;
    const y = (e.clientY - rect.top)  / zoom / cellSize;
    const col = Math.max(0, Math.min(hall.width,  Math.round(x)));
    const row = Math.max(0, Math.min(hall.height, Math.round(y)));
    return { col, row };
  };

  /* ── Annotation helpers (float cell coords) ───────────────────────────── */
  const svgToXY = (e) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = (e.clientX - rect.left) / zoom / cellSize;
    const y = (e.clientY - rect.top)  / zoom / cellSize;
    return { x, y };
  };

  // Ölçüm aracı için köşeye mıknatıslı yapışma: imleç bir grid/stand köşesine
  // (tam sayı hücre koordinatı) ekran-piksel yarıçapı içinde yaklaşınca o köşeye
  // kilitlenir. Piksel tabanlı olduğu için her zoom seviyesinde doğal mıknatıs hissi verir.
  const SNAP_PX = 12;
  const snapXY = (p) => {
    if (!p) return p;
    const thr = SNAP_PX / (cellSize * zoom); // px → hücre birimi
    const nx = Math.round(p.x), ny = Math.round(p.y);
    return {
      x: Math.abs(p.x - nx) <= thr ? nx : p.x,
      y: Math.abs(p.y - ny) <= thr ? ny : p.y,
      snappedX: Math.abs(p.x - nx) <= thr,
      snappedY: Math.abs(p.y - ny) <= thr,
    };
  };

  const saveAnnotations = useCallback(async (anns) => {
    try { await apiClient.put(`/ultra-admin/halls/${hallId}/annotations`, { annotations: anns }); }
    catch (e) { console.error(e); }
  }, [hallId]); // eslint-disable-line

  const commitAnnotations = (next) => { setAnnotations(next); annRef.current = next; saveAnnotations(next); };

  const deleteAnnotation = (id) => {
    const next = annRef.current.filter(a => a.id !== id);
    setSelectedAnnId(null);
    commitAnnotations(next);
  };

  const updateAnnotation = (id, patch, persist = true) => {
    const next = annRef.current.map(a => a.id === id ? { ...a, ...patch } : a);
    setAnnotations(next); annRef.current = next;
    if (persist) saveAnnotations(next);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post(`/ultra-admin/halls/${hallId}/annotation-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      const id = `ann-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      // Place at hall centre, default 6×3 cells (metre-agnostic; user resizes)
      const w = Math.min(6, hall.width / 2), h = Math.min(3, hall.height / 2);
      const ann = { id, type: 'image', x: hall.width / 2 - w / 2, y: hall.height / 2 - h / 2, w, h, url };
      commitAnnotations([...annRef.current, ann]);
      setSelectedAnnId(id);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Logo yüklenemedi.');
    } finally { setUploadingLogo(false); }
  };

  const uploadBackground = async (file) => {
    if (!file) return;
    setUploadingBg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post(`/ultra-admin/halls/${hallId}/background`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHall(prev => prev ? { ...prev, background: { url: res.data.url, path: res.data.path } } : prev);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Arka plan yüklenemedi.');
    } finally { setUploadingBg(false); }
  };

  const removeBackground = async () => {
    if (!hall?.background) return;
    if (!window.confirm(t('Remove the background image?','Arka plan görseli kaldırılsın mı?'))) return;
    try {
      await apiClient.delete(`/ultra-admin/halls/${hallId}/background`);
      setHall(prev => prev ? { ...prev, background: null } : prev);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Arka plan kaldırılamadı.');
    }
  };

  /* ── Annotation rendering (inside scaled SVG group) ───────────────────── */
  const annBeginDrag = (e, a, mode) => {
    e.stopPropagation();
    if (activeTool === 'eraser') { deleteAnnotation(a.id); return; }
    const p = svgToXY(e); if (!p) return;
    setSelectedAnnId(a.id);
    annDragRef.current = { mode, id: a.id, start: p, orig: { ...a } };
    setIsDragging(true);
  };

  const renderResizeHandle = (cx, cy, a) => {
    const s = 9 / zoom;
    return (
      <rect x={cx * cellSize - s / 2} y={cy * cellSize - s / 2} width={s} height={s}
        fill="#fff" stroke="#0891b2" strokeWidth={2 / zoom} style={{ cursor: 'nwse-resize' }}
        onMouseDown={(e) => annBeginDrag(e, a, 'resize')} />
    );
  };

  const renderOneAnnotation = (a) => {
    const sel = selectedAnnId === a.id;
    const CS = cellSize;
    const sw = (a.stroke || 2) / zoom * 1.5;
    const selRing = sel ? { filter: 'drop-shadow(0 0 2px #0891b2)' } : {};
    if (a.type === 'measure') {
      const mx = (a.x1 + a.x2) / 2, my = (a.y1 + a.y2) / 2;
      const dist = Math.hypot(a.x2 - a.x1, a.y2 - a.y1) * gridUnit;
      const label = `${dist.toFixed(dist % 1 === 0 ? 0 : 2)} m`;
      return (
        <g key={a.id} style={{ cursor: 'move' }} onMouseDown={(e) => annBeginDrag(e, a, 'move')}>
          <line x1={a.x1 * CS} y1={a.y1 * CS} x2={a.x2 * CS} y2={a.y2 * CS} stroke="#0891b2" strokeWidth={2 / zoom} strokeDasharray={`${6 / zoom},${4 / zoom}`} />
          {[[a.x1, a.y1], [a.x2, a.y2]].map(([px, py], i) => (
            <circle key={i} cx={px * CS} cy={py * CS} r={4 / zoom} fill="#0891b2" />
          ))}
          <g transform={`translate(${mx * CS}, ${my * CS})`}>
            <rect x={-28 / zoom} y={-22 / zoom} width={56 / zoom} height={16 / zoom} rx={4 / zoom} fill="#0891b2" />
            <text x={0} y={-10 / zoom} fill="#fff" fontSize={11 / zoom} fontWeight="700" textAnchor="middle" dominantBaseline="middle">{label}</text>
          </g>
          {sel && renderResizeHandle(a.x2, a.y2, a)}
        </g>
      );
    }
    if (a.type === 'line') {
      return (
        <g key={a.id} style={{ cursor: 'move', ...selRing }} onMouseDown={(e) => annBeginDrag(e, a, 'move')}>
          <line x1={a.x1 * CS} y1={a.y1 * CS} x2={a.x2 * CS} y2={a.y2 * CS} stroke={a.color} strokeWidth={sw} strokeLinecap="round" />
          <line x1={a.x1 * CS} y1={a.y1 * CS} x2={a.x2 * CS} y2={a.y2 * CS} stroke="transparent" strokeWidth={12 / zoom} />
          {sel && renderResizeHandle(a.x2, a.y2, a)}
        </g>
      );
    }
    if (a.type === 'rect') {
      return (
        <g key={a.id} style={{ cursor: 'move', ...selRing }} onMouseDown={(e) => annBeginDrag(e, a, 'move')}>
          <rect x={a.x * CS} y={a.y * CS} width={a.w * CS} height={a.h * CS} fill="none" stroke={a.color} strokeWidth={sw} />
          <rect x={a.x * CS} y={a.y * CS} width={a.w * CS} height={a.h * CS} fill="transparent" />
          {sel && renderResizeHandle(a.x + a.w, a.y + a.h, a)}
        </g>
      );
    }
    if (a.type === 'circle') {
      return (
        <g key={a.id} style={{ cursor: 'move', ...selRing }} onMouseDown={(e) => annBeginDrag(e, a, 'move')}>
          <circle cx={a.cx * CS} cy={a.cy * CS} r={a.r * CS} fill="none" stroke={a.color} strokeWidth={sw} />
          <circle cx={a.cx * CS} cy={a.cy * CS} r={a.r * CS} fill="transparent" />
          {sel && renderResizeHandle(a.cx + a.r, a.cy, a)}
        </g>
      );
    }
    if (a.type === 'text') {
      return (
        <g key={a.id} style={{ cursor: 'move', ...selRing }}
          onMouseDown={(e) => annBeginDrag(e, a, 'move')}
          onDoubleClick={(e) => { e.stopPropagation(); const t = window.prompt('Yazı', a.text); if (t != null) updateAnnotation(a.id, { text: t }); }}>
          <text x={a.x * CS} y={a.y * CS} fill={a.color} fontSize={a.fontSize / zoom * zoom} fontFamily={a.fontFamily} fontWeight="600" dominantBaseline="hanging" style={{ userSelect: 'none' }}>{a.text}</text>
          {sel && <rect x={a.x * CS - 2} y={a.y * CS - 2} width={Math.max(20, (a.text?.length || 1) * a.fontSize * 0.6)} height={a.fontSize + 4} fill="none" stroke="#0891b2" strokeWidth={1 / zoom} strokeDasharray={`${3 / zoom},${3 / zoom}`} />}
        </g>
      );
    }
    if (a.type === 'image') {
      return (
        <g key={a.id} style={{ cursor: 'move', ...selRing }} onMouseDown={(e) => annBeginDrag(e, a, 'move')}>
          <image href={a.url} x={a.x * CS} y={a.y * CS} width={a.w * CS} height={a.h * CS} preserveAspectRatio="xMidYMid meet" crossOrigin="anonymous" />
          {sel && <rect x={a.x * CS} y={a.y * CS} width={a.w * CS} height={a.h * CS} fill="none" stroke="#0891b2" strokeWidth={1.5 / zoom} strokeDasharray={`${4 / zoom},${3 / zoom}`} />}
          {sel && renderResizeHandle(a.x + a.w, a.y + a.h, a)}
        </g>
      );
    }
    return null;
  };

  const renderAnnotations = () => annotations.map(renderOneAnnotation);

  const renderAnnDraft = () => {
    const d = annDraft; if (!d) return null;
    const CS = cellSize;
    if (d.type === 'rect') {
      const x = Math.min(d.x, d.x + d.w), y = Math.min(d.y, d.y + d.h);
      return <rect x={x * CS} y={y * CS} width={Math.abs(d.w) * CS} height={Math.abs(d.h) * CS} fill="none" stroke={d.color} strokeWidth={2 / zoom} strokeDasharray={`${5 / zoom},${4 / zoom}`} />;
    }
    if (d.type === 'circle') {
      return <circle cx={d.cx * CS} cy={d.cy * CS} r={d.r * CS} fill="none" stroke={d.color} strokeWidth={2 / zoom} strokeDasharray={`${5 / zoom},${4 / zoom}`} />;
    }
    // line / measure
    const isMeasure = d.type === 'measure';
    const dist = Math.hypot(d.x2 - d.x1, d.y2 - d.y1) * gridUnit;
    const endSnapped = isMeasure && Number.isInteger(d.x2) && Number.isInteger(d.y2);
    const startSnapped = isMeasure && Number.isInteger(d.x1) && Number.isInteger(d.y1);
    return (
      <g>
        <line x1={d.x1 * CS} y1={d.y1 * CS} x2={d.x2 * CS} y2={d.y2 * CS} stroke={isMeasure ? '#0891b2' : d.color} strokeWidth={2 / zoom} strokeDasharray={`${5 / zoom},${4 / zoom}`} />
        {isMeasure && (
          <>
            <circle cx={d.x1 * CS} cy={d.y1 * CS} r={3 / zoom} fill="#0891b2" />
            <circle cx={d.x2 * CS} cy={d.y2 * CS} r={3 / zoom} fill="#0891b2" />
            {startSnapped && <circle cx={d.x1 * CS} cy={d.y1 * CS} r={7 / zoom} fill="none" stroke="#f59e0b" strokeWidth={2 / zoom} />}
            {endSnapped && <circle cx={d.x2 * CS} cy={d.y2 * CS} r={7 / zoom} fill="none" stroke="#f59e0b" strokeWidth={2 / zoom} />}
            <text x={(d.x1 + d.x2) / 2 * CS} y={(d.y1 + d.y2) / 2 * CS - 8 / zoom} fill="#0891b2" fontSize={12 / zoom} fontWeight="700" textAnchor="middle">{dist.toFixed(dist % 1 === 0 ? 0 : 2)} m</text>
          </>
        )}
      </g>
    );
  };



  /* ── Infrastructure helpers ───────────────────────────────────────────── */
  const saveInfra = useCallback(async (map) => {
    const items = [];
    map.forEach((item) => {
      items.push({ id: item.id, type: item.type, col: item.col, row: item.row, width: item.width, height: item.height });
    });
    try {
      await apiClient.put(`/ultra-admin/halls/${hallId}/infra`, { items });
    } catch (e) { console.error(e); }
  }, [hallId]); // eslint-disable-line

  const saveColumns = useCallback(async (cols) => {
    try {
      await apiClient.put(`/ultra-admin/halls/${hallId}/columns`, { columns: cols });
    } catch (e) { console.error(e); }
  }, [hallId]); // eslint-disable-line

  const addColumn = () => {
    if (!hall) return;
    const id = `col-${Date.now()}`;
    const newCol = {
      id,
      shape: colForm.shape,
      col: Math.floor(hall.width / 2),
      row: Math.floor(hall.height / 2),
      w: colForm.shape === 'daire' ? colForm.d : colForm.w,
      h: colForm.shape === 'daire' ? colForm.d : (colForm.shape === 'kare' ? colForm.w : colForm.h),
      d: colForm.d,
    };
    const next = [...columns, newCol];
    setColumns(next);
    saveColumns(next);
    setColumnModal(false);
    setColForm({ shape: 'kare', w: 1, h: 1, d: 1 });
  };

  const deleteColumn = (id) => {
    const next = columns.filter(c => c.id !== id);
    setColumns(next);
    saveColumns(next);
  };

  // Silgi: tıklanan hücreyi kapsayan dikdörtgen kaydı sil
  const eraseInfraAt = useCallback((col, row) => {
    setInfraItems(prev => {
      const next = new Map(prev);
      for (const [id, item] of next.entries()) {
        if (col >= item.col && col < item.col + item.width &&
            row >= item.row && row < item.row + item.height) {
          next.delete(id);
          break;
        }
      }
      saveInfra(next);
      return next;
    });
  }, [saveInfra]); // eslint-disable-line

  /* ── Undo / Redo ──────────────────────────────────────────────────────── */
  const undoInfra = () => {
    const idx = histIdxRef.current;
    if (idx <= 0) return;
    const prev = histSnapshots.current[idx - 1];
    setInfraItems(new Map(prev));
    histIdxRef.current = idx - 1;
    setHistState({ canUndo: histIdxRef.current > 0, canRedo: histIdxRef.current < histSnapshots.current.length - 1 });
    saveInfra(prev);
  };

  const redoInfra = () => {
    const idx = histIdxRef.current;
    if (idx >= histSnapshots.current.length - 1) return;
    const next = histSnapshots.current[idx + 1];
    setInfraItems(new Map(next));
    histIdxRef.current = idx + 1;
    setHistState({ canUndo: histIdxRef.current > 0, canRedo: histIdxRef.current < histSnapshots.current.length - 1 });
    saveInfra(next);
  };

  /* ── Mouse handlers ───────────────────────────────────────────────────── */
  const onMouseDown = (e) => {
    if (e.button !== 0) return;

    // Pan tool: container scroll başlat
    if (activeTool === 'pan') {
      const el = containerRef.current;
      if (!el) return;
      panRef.current = { startX: e.clientX, startY: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
      el.style.cursor = 'grabbing';
      return;
    }

    // Annotation draw / text placement
    if (ANN_DRAW_TOOLS.includes(activeTool)) {
      const p = svgToXY(e); if (!p) return;
      setSelectedAnnId(null);
      if (activeTool === 'measure')        { const s = snapXY(p); setAnnDraft({ type: 'measure', x1: s.x, y1: s.y, x2: s.x, y2: s.y }); }
      else if (activeTool === 'ann_line')  setAnnDraft({ type: 'line',   x1: p.x, y1: p.y, x2: p.x, y2: p.y, color: annStyle.color, stroke: 2 });
      else if (activeTool === 'ann_rect')  setAnnDraft({ type: 'rect',   x: p.x, y: p.y, w: 0, h: 0, color: annStyle.color, stroke: 2 });
      else if (activeTool === 'ann_circle')setAnnDraft({ type: 'circle', cx: p.x, cy: p.y, r: 0, color: annStyle.color, stroke: 2 });
      setIsDragging(true);
      return;
    }
    if (activeTool === 'ann_text') {
      const p = svgToXY(e); if (!p) return;
      const id = `ann-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      commitAnnotations([...annRef.current, { id, type: 'text', x: p.x, y: p.y, text: (language === 'en' ? 'Text' : 'Metin'), fontSize: annStyle.fontSize, fontFamily: annStyle.fontFamily, color: annStyle.color }]);
      setSelectedAnnId(id);
      setActiveTool('stand');
      return;
    }

    const cell = svgToCell(e);
    if (!cell) return;

    if (activeTool === 'stand') {
      const hitStand = occupiedMap[cellKey(cell.col, cell.row)];

      if (selectedStand && hitStand?.id === selectedStand.id) {
        // Mousedown on selected stand → start move
        const cells = selectedStand.cells || [];
        const cols  = cells.map(c => c.col);
        const rows  = cells.map(c => c.row);
        setInteractMode('move');
        setInteractStart({
          cell,
          originalCells:  cells.map(c => ({ ...c })),
          originalBounds: {
            minC: Math.min(...cols), maxC: Math.max(...cols),
            minR: Math.min(...rows), maxR: Math.max(...rows),
          },
        });
        setIsDragging(true);
      } else if (hitStand) {
        // Different stand → just select it
        setSelectedStand(hitStand);
      } else {
        // Empty cell → draw new stand (köşe-mıknatısı: en yakın grid köşesinden başlat)
        const corner = svgToCorner(e) || cell;
        setSelectedStand(null);
        setSelectedColId(null); // kolon seçimini temizle
        setSelectedInfraId(null); // altyapı seçimini temizle
        setDragStart(corner);
        setDragEnd(corner);
        setIsDragging(true);
      }
    } else if (activeTool === 'eraser') {
      // Silgi: tıklanan hücreyi kapsayan dikdörtgeni sil
      setIsDragging(true);
      eraseInfraAt(cell.col, cell.row);
    } else if (activeTool === 'fire_cabinet' || activeTool === 'water' || activeTool === 'electricity') {
      // Yangın Dolabı, Su & Elektrik: tek tıkla 1×1 yerleştir
      const next = new Map(infraRef.current);
      const id = `rect-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      next.set(id, { id, type: activeTool, col: cell.col, row: cell.row, width: 1, height: 1 });
      setInfraItems(next);
      pushHistory(next);
      saveInfra(next);
    } else {
      // Infrastructure tools (corridor, door, etc.): rectangle drag
      setSelectedStand(null);
      setDragStart(cell);
      setDragEnd(cell);
      setIsDragging(true);
    }
  };

  const onMouseMove = (e) => {
    // Annotation drawing draft
    if (annDraft) {
      const p = svgToXY(e); if (!p) return;
      setAnnDraft(d => {
        if (!d) return d;
        if (d.type === 'rect')   return { ...d, w: p.x - d.x, h: p.y - d.y };
        if (d.type === 'circle') return { ...d, r: Math.hypot(p.x - d.cx, p.y - d.cy) };
        if (d.type === 'measure') { const s = snapXY(p); return { ...d, x2: s.x, y2: s.y }; }
        return { ...d, x2: p.x, y2: p.y };
      });
      return;
    }
    // Annotation move / resize
    if (annDragRef.current) {
      const p = svgToXY(e); if (!p) return;
      const { mode, id, start, orig: a } = annDragRef.current;
      const dx = p.x - start.x, dy = p.y - start.y;
      if (mode === 'move') {
        if (a.type === 'line' || a.type === 'measure') updateAnnotation(id, { x1: a.x1 + dx, y1: a.y1 + dy, x2: a.x2 + dx, y2: a.y2 + dy }, false);
        else if (a.type === 'circle')                  updateAnnotation(id, { cx: a.cx + dx, cy: a.cy + dy }, false);
        else                                            updateAnnotation(id, { x: a.x + dx, y: a.y + dy }, false);
      } else { // resize
        if (a.type === 'measure')                      { const s = snapXY({ x: a.x2 + dx, y: a.y2 + dy }); updateAnnotation(id, { x2: s.x, y2: s.y }, false); }
        else if (a.type === 'line')                    updateAnnotation(id, { x2: a.x2 + dx, y2: a.y2 + dy }, false);
        else if (a.type === 'circle')                  updateAnnotation(id, { r: Math.max(0.2, a.r + dx) }, false);
        else                                            updateAnnotation(id, { w: Math.max(0.5, a.w + dx), h: Math.max(0.5, a.h + dy) }, false);
      }
      return;
    }
    // Pan: container scroll
    if (activeTool === 'pan' && panRef.current) {
      const el = containerRef.current;
      if (!el) return;
      const dx = e.clientX - panRef.current.startX;
      const dy = e.clientY - panRef.current.startY;
      el.scrollLeft = panRef.current.scrollLeft - dx;
      el.scrollTop  = panRef.current.scrollTop  - dy;
      return;
    }
    // Column drag
    if (draggingColRef.current) {
      const cell = svgToCell(e);
      if (!cell || !hall) return;
      const { id, offC, offR } = draggingColRef.current;
      setColumns(prev => prev.map(c =>
        c.id === id ? { ...c,
          col: Math.max(0, Math.min(hall.width  - 0.5, cell.col - offC)),
          row: Math.max(0, Math.min(hall.height - 0.5, cell.row - offR)),
        } : c
      ));
      return;
    }
    if (!isDragging) return;
    const cell = svgToCell(e);
    if (!cell) return;

    if (interactMode === 'move' && interactStart && hall) {
      const dC = cell.col - interactStart.cell.col;
      const dR = cell.row - interactStart.cell.row;
      const newCells = interactStart.originalCells.map(c => ({
        col: Math.max(0, Math.min(hall.width  - 1, c.col + dC)),
        row: Math.max(0, Math.min(hall.height - 1, c.row + dR)),
      }));
      setPreviewCells(newCells);
    } else if (interactMode === 'resize' && interactStart && hall) {
      const { handle, originalBounds: ob } = interactStart;
      const dC = cell.col - interactStart.cell.col;
      const dR = cell.row - interactStart.cell.row;
      let { minC, maxC, minR, maxR } = ob;
      if (handle.includes('w')) minC = Math.max(0,              Math.min(ob.minC + dC, ob.maxC));
      if (handle.includes('e')) maxC = Math.max(ob.minC,        Math.min(hall.width  - 1, ob.maxC + dC));
      if (handle.includes('n')) minR = Math.max(0,              Math.min(ob.minR + dR, ob.maxR));
      if (handle.includes('s')) maxR = Math.max(ob.minR,        Math.min(hall.height - 1, ob.maxR + dR));
      const newCells = [];
      for (let c = minC; c <= maxC; c++)
        for (let r = minR; r <= maxR; r++)
          newCells.push({ col: c, row: r });
      setPreviewCells(newCells);
    } else if (activeTool === 'stand') {
      setDragEnd(svgToCorner(e) || cell);
    } else if (activeTool !== 'eraser') {
      // Infra rectangle drag: update dragEnd
      setDragEnd(cell);
    } else {
      // Silgi: sürükle ile sil
      eraseInfraAt(cell.col, cell.row);
    }
  };

  const onMouseUp = useCallback(async () => {
    // Annotation draft commit
    if (annDraftRef.current) {
      const d = annDraftRef.current;
      setAnnDraft(null); setIsDragging(false);
      const id = `ann-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      let ann = null;
      if (d.type === 'rect') {
        const x = Math.min(d.x, d.x + d.w), y = Math.min(d.y, d.y + d.h), w = Math.abs(d.w), h = Math.abs(d.h);
        if (w >= 0.3 && h >= 0.3) ann = { id, type: 'rect', x, y, w, h, color: d.color, stroke: d.stroke };
      } else if (d.type === 'circle') {
        if (d.r >= 0.2) ann = { id, type: 'circle', cx: d.cx, cy: d.cy, r: d.r, color: d.color, stroke: d.stroke };
      } else {
        if (Math.hypot(d.x2 - d.x1, d.y2 - d.y1) >= 0.2) {
          ann = d.type === 'measure'
            ? { id, type: 'measure', x1: d.x1, y1: d.y1, x2: d.x2, y2: d.y2 }
            : { id, type: 'line', x1: d.x1, y1: d.y1, x2: d.x2, y2: d.y2, color: d.color, stroke: d.stroke };
        }
      }
      if (ann) { commitAnnotations([...annRef.current, ann]); setSelectedAnnId(id); }
      return;
    }
    // Annotation move/resize end → persist
    if (annDragRef.current) {
      annDragRef.current = null;
      setIsDragging(false);
      saveAnnotations(annRef.current);
      return;
    }
    // Pan end
    if (panRef.current) {
      panRef.current = null;
      if (containerRef.current) containerRef.current.style.cursor = '';
    }
    // Column drag end → save
    if (draggingColRef.current) {
      draggingColRef.current = null;
      setDraggingCol(null);
      setColumns(prev => { saveColumns(prev); return prev; });
      return;
    }
    if (isDragging) {
      if (interactMode === 'move' || interactMode === 'resize') {
        // ── Save move / resize to backend ──────────────────────────────
        if (previewCells && previewCells.length > 0 && selectedStand) {
          const stand = stands.find(s => s.id === selectedStand.id);
          if (stand) {
            const payload = {
              stand_no:         stand.stand_no,
              stand_name:       stand.stand_name || '',
              cells:            previewCells,
              status:           stand.status || 'available',
              color:            stand.color   || null,
              show_area:        stand.show_area !== false,
              price_per_sqm:    stand.price_per_sqm    || null,
              currency:         stand.currency          || 'USD',
              ceiling_height:   stand.ceiling_height    || null,
              max_stand_height: stand.max_stand_height  || null,
              hide_status:      stand.hide_status  || false,
              hide_company:     stand.hide_company || false,
              open_sides:       stand.open_sides != null ? stand.open_sides : ['north', 'south', 'east', 'west'],
            };
            try {
              await apiClient.put(`/ultra-admin/halls/${hallId}/stands/${stand.id}`, payload);
              await fetchHall();
              setSelectedStand(prev => prev ? { ...prev, cells: previewCells } : null);
            } catch (err) { console.error(err); }
          }
        }
        setInteractMode(null);
        setInteractStart(null);
        setPreviewCells(null);
      } else if (activeTool === 'stand' && dragStart && dragEnd) {
        // Köşe modeli: dragStart/dragEnd birer grid KÖŞESİ. Hücreler köşeler arasında kalanlar.
        const minCol = Math.min(dragStart.col, dragEnd.col);
        const maxCol = Math.max(dragStart.col, dragEnd.col);
        const minRow = Math.min(dragStart.row, dragEnd.row);
        const maxRow = Math.max(dragStart.row, dragEnd.row);
        const dragged = (maxCol - minCol) >= 1 && (maxRow - minRow) >= 1;

        if (dragged) {
          const cells = [];
          for (let c = minCol; c < maxCol; c++)
            for (let r = minRow; r < maxRow; r++)
              cells.push({ col: c, row: r });
          const newModal = emptyModal();
          newModal.color        = STAND_COLORS[stands.length % STAND_COLORS.length];
          newModal.transparency = gridSettings.transparency;
          // Pre-fill with hall defaults
          if (hall?.default_price_per_sqm)  newModal.price_per_sqm    = hall.default_price_per_sqm;
          if (hall?.default_currency)        newModal.currency          = hall.default_currency;
          if (hall?.ceiling_height != null)  newModal.ceiling_height    = hall.ceiling_height;
          if (hall?.max_stand_height != null) newModal.max_stand_height = hall.max_stand_height;
          if (hall?.package_price_per_sqm != null) newModal.price_per_sqm = hall.package_price_per_sqm;
          setPendingCells(cells);
          setEditingStand(null);
          setModalData(newModal);
          setModalTenantId(null);
          setModalTenantSearch('');
          setShowModalTenantDrop(false);
          fetchTenants();
          setShowModal(true);
        } else {
          // Tek tık → köşe koordinatını hücreye kırpıp o hücredeki standı seç
          const cc = Math.min(dragStart.col, (hall?.width  || 1) - 1);
          const rr = Math.min(dragStart.row, (hall?.height || 1) - 1);
          const stand = occupiedMap[cellKey(cc, rr)];
          setSelectedStand(stand || null);
        }
      } else if (activeTool !== 'stand' && activeTool !== 'eraser' && activeTool !== 'fire_cabinet' && activeTool !== 'water' && activeTool !== 'electricity' && dragStart && dragEnd) {
        // ── Rectangle infra fill ──────────────────────────────────────────
        const minCol = Math.min(dragStart.col, dragEnd.col);
        const maxCol = Math.max(dragStart.col, dragEnd.col);
        const minRow = Math.min(dragStart.row, dragEnd.row);
        const maxRow = Math.max(dragStart.row, dragEnd.row);
        const next = new Map(infraRef.current);
        const id = `rect-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        next.set(id, { id, type: activeTool, col: minCol, row: minRow, width: maxCol - minCol + 1, height: maxRow - minRow + 1 });
        setInfraItems(next);   // ← React state güncelle (SVG render için)
        pushHistory(next);
        saveInfra(next);
      } else if (activeTool === 'eraser') {
        // Silgi: drag ile silme (mousedown'da handleClick zaten çalışır)
        const current = infraRef.current;
        pushHistory(current);
        saveInfra(current);
      }
    }
    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  }, [isDragging, interactMode, previewCells, selectedStand, stands, activeTool, dragStart, dragEnd, gridSettings.transparency, occupiedMap, pushHistory, saveInfra, hallId, fetchHall]); // eslint-disable-line

  /* ── Save stand (create or update) ───────────────────────────────────── */
  const saveStand = async () => {
    setSavingStand(true);
    const payload = {
      stand_no:       modalData.stand_no.trim(),
      stand_name:     modalData.stand_name.trim(),
      cells:          editingStand ? editingStand.cells : pendingCells,
      status:         modalData.status,
      color:          modalData.color,
      show_area:      modalData.show_area,
      price_per_sqm:  modalData.price_per_sqm ? parseFloat(modalData.price_per_sqm) : null,
      currency:       modalData.currency,
      ceiling_height:   modalData.ceiling_height ? parseFloat(modalData.ceiling_height) : null,
      max_stand_height: modalData.max_stand_height ? parseFloat(modalData.max_stand_height) : null,
      hide_status:    modalData.hide_status,
      hide_company:   modalData.hide_company,
      open_sides:     modalData.open_sides || ['north', 'south', 'east', 'west'],
      // Phase 86AU — only forward stand_type when explicitly set. Empty
      // string clears any previous tag on the stand.
      stand_type:     modalData.stand_type || null,
      // Phase 86AZ.2 — Forward the modular package binding. Backend ignores
      // it unless stand_type='modular' and validates area match.
      assigned_modular_package_id: modalData.assigned_modular_package_id || null,
    };
    try {
      let targetStandId = editingStand?.id;
      if (editingStand) {
        await apiClient.put(`/ultra-admin/halls/${hallId}/stands/${editingStand.id}`, payload);
      } else {
        const res = await apiClient.post(`/ultra-admin/halls/${hallId}/stands`, payload);
        targetStandId = res.data?.id;
      }

      // Handle tenant assignment change from modal
      if (targetStandId) {
        const prevTenantId = editingStand?.tenant_id || null;
        if (modalTenantId && modalTenantId !== prevTenantId) {
          // Assign new tenant
          await apiClient.post(`/ultra-admin/halls/${hallId}/stands/${targetStandId}/assign`,
            { tenant_id: modalTenantId });
        } else if (!modalTenantId && prevTenantId) {
          // Unassign tenant
          await apiClient.delete(`/ultra-admin/halls/${hallId}/stands/${targetStandId}/assign`);
        }
      }

      setShowModal(false);
      setPendingCells([]);
      const savedId = targetStandId;
      setEditingStand(null);
      setModalTenantId(null);
      setModalTenantSearch('');
      const freshHall = await fetchHall();
      if (savedId && freshHall) {
        const refreshed = (freshHall.stands || []).find(s => s.id === savedId);
        if (refreshed) setSelectedStand(refreshed);
      } else {
        setSelectedStand(null);
      }
    } catch (e) {
      // Surface backend rule violations (e.g. modular area mismatch).
      const msg = e?.response?.data?.detail || e?.message;
      if (msg) toast.error(typeof msg === 'string' ? msg : (tr ? 'Stand kaydedilemedi.' : 'Could not save stand.'));
      console.error(e);
    }
    setSavingStand(false);
  };

  /* ── Copy stand ───────────────────────────────────────────────────────── */
  const copyStand = async (stand) => {
    const offset = 2;
    const cells = (stand.cells || []).map(c => ({ col: c.col + offset, row: c.row + offset }));
    // Check bounds
    if (!hall || cells.some(c => c.col >= hall.width || c.row >= hall.height)) {
      alert(tr ? 'Kopyalamak için alan yok. Başka bir konuma yerleştirin.' : 'No space to copy here. Place it in a different location.');
      return;
    }

    // Build a new stand_no by incrementing the trailing number on the source.
    // E.g. "A-200" → "A-201" → "A-202" ... If a candidate is already taken,
    // skip until we find a free slot. This keeps stands cleanly numbered
    // instead of producing "A-200-kopi-kopi-kopi" chains.
    const existing = new Set(((hall && hall.stands) || []).map(s => (s.stand_no || '').toLowerCase()));
    const buildNextNo = (baseNo) => {
      const m = String(baseNo || '').match(/^(.*?)(\d+)(\D*)$/);
      if (!m) return `${baseNo || 'STAND'}-1`;
      const prefix = m[1];        // "A-"
      const numStr = m[2];         // "200"
      const suffix = m[3];         // ""
      const pad = numStr.length;   // preserve zero-padding (e.g. "001" stays 3-digit)
      let n = parseInt(numStr, 10) + 1;
      // Skip already-used numbers
      while (existing.has((`${prefix}${String(n).padStart(pad, '0')}${suffix}`).toLowerCase())) {
        n += 1;
      }
      return `${prefix}${String(n).padStart(pad, '0')}${suffix}`;
    };

    const payload = {
      stand_no: buildNextNo(stand.stand_no),
      stand_name: stand.stand_name || '',
      cells,
      status: stand.status || 'available',
      color: stand.color,
      show_area: stand.show_area,
      price_per_sqm: stand.price_per_sqm,
      currency: stand.currency || 'USD',
      ceiling_height: stand.ceiling_height,
      max_stand_height: stand.max_stand_height,
      hide_status: stand.hide_status || false,
      hide_company: stand.hide_company || false,
    };
    await apiClient.post(`/ultra-admin/halls/${hallId}/stands`, payload);
    fetchHall();
  };

  /* ── Delete stand ─────────────────────────────────────────────────────── */
  const deleteStand = async (id) => {
    if (!window.confirm(tr ? 'Bu stand silinsin mi?' : 'Delete this stand?')) return;
    await apiClient.delete(`/ultra-admin/halls/${hallId}/stands/${id}`);
    setSelectedStand(null);
    fetchHall();
  };

  /* ── Status update ────────────────────────────────────────────────────── */
  const updateStatus = async (status) => {
    if (!selectedStand) return;
    // If trying to set available on a stand with tenant → show confirmation
    if (status === 'available' && selectedStand.tenant_id) {
      setUnassignSource('panel');
      setShowUnassignConfirm(true);
      return;
    }
    await apiClient.patch(`/ultra-admin/halls/${hallId}/stands/${selectedStand.id}/status`, { status });
    await fetchHall();
    setSelectedStand(s => ({ ...s, status }));
  };

  /* ── Unassign confirm handler ─────────────────────────────────────────── */
  const handleUnassignConfirm = async () => {
    setShowUnassignConfirm(false);
    if (unassignSource === 'panel') {
      await unassignTenant();
    } else if (unassignSource === 'modal') {
      setModalTenantId(null);
      setModalTenantSearch('');
      setModalData(p => ({ ...p, status: 'available' }));
    }
    setUnassignSource(null);
  };

  /* ── Assign ───────────────────────────────────────────────────────────── */
  const fetchTenants = async () => {
    try { const res = await apiClient.get(`/ultra-admin/halls-tenants`); setTenants(res.data); } catch {}
  };

  const assignTenant = async (tenantId, force = false) => {
    if (!selectedStand) return;
    // If tenant is already assigned somewhere AND user hasn't confirmed yet,
    // show the move-confirmation modal.
    if (!force) {
      const t = (tenants || []).find(x => x.id === tenantId);
      if (t && t.assigned_stand) {
        setMoveConfirm({
          tenantId,
          tenantName: t.name,
          fromStand: t.assigned_stand,
          fromHall: t.assigned_hall_name,
          toStand: selectedStand.stand_no,
          toHall: hall?.name || '',
        });
        return;
      }
    }
    setAssigning(true);
    try {
      const standId = selectedStand.id;
      // Phase 86AU — only send stand_type when this hall is Green Zone (per
      // product spec — feature scoped to Green Zone for now). For Blue Zone
      // we keep the previous behaviour to avoid disturbing the existing
      // modular-stand wizard flow.
      const payload = { tenant_id: tenantId };
      if (hall?.zone === 'green_zone') {
        payload.stand_type = assignStandType || 'space_only';
        // Phase 86AZ.2 — only forward the package binding for modular stands.
        if (assignStandType === 'modular' && assignModularPkgId) {
          payload.assigned_modular_package_id = assignModularPkgId;
        }
      }
      await apiClient.post(`/ultra-admin/halls/${hallId}/stands/${standId}/assign`, payload);
      setShowAssignModal(false); setTenantSearch('');
      setMoveConfirm(null);
      const freshHall = await fetchHall();
      const freshStand = (freshHall?.stands || []).find(s => s.id === standId);
      if (freshStand) setSelectedStand(freshStand);
    } catch (e) {
      // Surface the backend rule violation (e.g. modular area mismatch) so the
      // admin sees exactly why the assignment failed.
      const msg = e?.response?.data?.detail || e?.message;
      if (msg) toast.error(typeof msg === 'string' ? msg : 'Atama başarısız.');
      console.error(e);
    }
    setAssigning(false);
  };

  const unassignTenant = async () => {
    if (!selectedStand) return;
    const standId = selectedStand.id;
    await apiClient.delete(`/ultra-admin/halls/${hallId}/stands/${standId}/assign`);
    const freshHall = await fetchHall();
    const freshStand = (freshHall?.stands || []).find(s => s.id === standId);
    setSelectedStand(freshStand || null);
  };

  /* ── Open edit modal ──────────────────────────────────────────────────── */
  const openEditModal = (stand) => {
    setEditingStand(stand);
    setModalData({
      stand_no:         stand.stand_no || '',
      stand_name:       stand.stand_name || '',
      status:           stand.status || 'available',
      color:            stand.color || STAND_COLORS[0],
      transparency:     stand.transparency || gridSettings.transparency,
      show_area:        stand.show_area !== false,
      price_per_sqm:    stand.price_per_sqm || '',
      currency:         stand.currency || 'USD',
      ceiling_height:   stand.ceiling_height || '',
      max_stand_height: stand.max_stand_height || '',
      hide_status:      stand.hide_status || false,
      hide_company:     stand.hide_company || false,
      stand_type:       stand.stand_type || '',
      assigned_modular_package_id: stand.assigned_modular_package_id || '',
    });
    // Pre-fill modal tenant picker
    setModalTenantId(stand.tenant_id || null);
    setModalTenantSearch(stand.tenant_name || '');
    setShowModalTenantDrop(false);
    fetchTenants();
    setShowModal(true);
  };

  /* ── PNG export ───────────────────────────────────────────────────────── */
  const exportPNG = useCallback(async () => {
    const svgEl = svgRef.current;
    if (!svgEl || !hall) return;
    const clone = svgEl.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    // NOT: SVG kök zeminini beyaz yapmıyoruz — canvas zaten beyazla dolduruluyor.
    // Beyaz yaparsak canvas'a çizilen arka plan görselini örter.
    clone.style.background = 'transparent';
    // Arka plan görselini SVG'ye GÖMME — büyük görseller base64 olarak gömülünce
    // Image() içinde yüklenemiyor ve export sessizce başarısız oluyordu. Bunun yerine
    // arka planı canvas'a AYRI olarak çizeceğiz (same-origin → canvas taint olmaz).
    const bgNode = clone.querySelector('[data-hall-bg]');
    if (bgNode) bgNode.remove();
    const bgUrl = hall.background?.url ? `${process.env.REACT_APP_BACKEND_URL}${hall.background.url}` : null;
    // Arka plan varsa: opak hall zemin dikdörtgenini şeffaf yap, yoksa canvas'a
    // çizdiğimiz arka planı örter.
    if (bgUrl) {
      const bgRect = clone.querySelector('[data-hall-bgrect]');
      if (bgRect) bgRect.setAttribute('fill', 'none');
    }
    // Diğer <image>'ları (logo vb.) data URI olarak gömüyoruz.
    try {
      const imgs = Array.from(clone.querySelectorAll('image'));
      await Promise.all(imgs.map(async (node) => {
        const href = node.getAttribute('href') || node.getAttribute('xlink:href');
        if (!href || href.startsWith('data:')) return;
        try {
          const resp = await fetch(href);
          const blob = await resp.blob();
          const dataUrl = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(blob); });
          node.setAttribute('href', dataUrl);
          node.setAttribute('xlink:href', dataUrl);
        } catch (_) { node.remove(); /* ulaşılamayan görseli çıkar → taint olmasın */ }
      }));
    } catch (_) { /* ignore */ }
    const svgStr = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const img  = new Image();
    const scale = 2;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width  = baseSvgW * zoom * scale;
      canvas.height = baseSvgH * zoom * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);

      const finish = () => {
        ctx.drawImage(img, 0, 0, baseSvgW * zoom, baseSvgH * zoom);
        try {
          const a = document.createElement('a');
          a.download = `${hall.name.replace(/\s+/g, '_')}_layout.png`;
          a.href     = canvas.toDataURL('image/png');
          a.click();
        } catch (err) {
          alert('PNG oluşturulamadı: ' + (err?.message || err));
        }
        URL.revokeObjectURL(url);
      };

      if (bgUrl) {
        const bgImg = new Image();
        bgImg.onload = () => {
          // "meet" (en-boy oranı korunur) → hall alanına ortalanmış sığdır
          const W = (hall.width  * cellSize) * zoom;
          const H = (hall.height * cellSize) * zoom;
          const s = Math.min(W / bgImg.width, H / bgImg.height);
          const dw = bgImg.width * s, dh = bgImg.height * s;
          ctx.drawImage(bgImg, (W - dw) / 2, (H - dh) / 2, dw, dh);
          finish();
        };
        bgImg.onerror = () => finish(); // arka plan yüklenemezse arka plansız devam et
        bgImg.src = bgUrl;
      } else {
        finish();
      }
    };
    img.onerror = () => {
      alert('PNG oluşturulamadı (çizim yüklenemedi).');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [baseSvgW, baseSvgH, zoom, hall, cellSize]);

  /* ── Infrastructure SVG rendering ─────────────────────────────────────── */
  const SINGLE_CELL_TYPES = ['fire_cabinet', 'water', 'electricity']; // tıkla-seç-sil öğeler

  const renderInfraRect = (item) => {
    const info = INFRA_TYPES[item.type];
    if (!info) return null;
    const x  = item.col    * cellSize;
    const y  = item.row    * cellSize;
    const rw = item.width  * cellSize;
    const rh = item.height * cellSize;
    const cx = x + rw / 2;
    const cy = y + rh / 2;
    const lw = Math.max(1, 1.5 / zoom);
    const minDim = Math.min(rw, rh);
    const isSingleCell = SINGLE_CELL_TYPES.includes(item.type);
    const isSelected   = selectedInfraId === item.id;

    return (
      <g key={item.id}
        style={{ pointerEvents: isSingleCell ? 'all' : 'none', cursor: isSingleCell ? 'pointer' : 'default' }}
        onMouseDown={isSingleCell ? (e) => { e.stopPropagation(); setSelectedInfraId(prev => prev === item.id ? null : item.id); setSelectedColId(null); } : undefined}>
        {/* Dolgu — kapı için saydam, diğerleri için info.fill */}
        <rect x={x} y={y} width={rw} height={rh}
          fill={item.type === 'door' ? 'transparent' : info.fill} />
        {/* Koridor: çapraz çizgiler (hatch pattern) */}
        {item.type === 'corridor' && (
          <g stroke={info.border} strokeWidth={lw * 0.6} opacity="0.35">
            {Array.from({ length: Math.ceil((rw + rh) / (cellSize * 2)) }, (_, i) => {
              const offset = i * cellSize * 2;
              return (
                <line key={i}
                  x1={x + offset} y1={y}
                  x2={x} y2={y + offset}
                  clipPath={`inset(0 0 0 0)`}
                />
              );
            })}
          </g>
        )}
        {/* Kenar çerçevesi — kapı için kalın kesik çizgi (mimari planlarda kapı boşluğu) */}
        {item.type === 'door' ? (
          <rect x={x} y={y} width={rw} height={rh}
            fill="none" stroke={info.border}
            strokeWidth={lw * 1.6}
            strokeDasharray={`${cellSize * 0.4 / 1},${cellSize * 0.2 / 1}`}
            opacity="0.55" />
        ) : (
          <rect x={x} y={y} width={rw} height={rh} fill="none" stroke={info.border} strokeWidth={lw} />
        )}
        {/* İkon veya etiket (yeterince büyükse) */}
        {item.type === 'door' ? (
          /* ── KAPI: minimalist DoorOpen sembolü + otomatik numara ─────────
             Mimari floor-plan referansı: hücre saydamdır, kenarda kesik
             çizgi (= duvar açıklığı), ortada kapı silueti, altında D-N
             rozeti. Renk: tek bir koyu antrasit (info.border). */
          <g pointerEvents="none">
            {/* DoorOpen silueti — Lucide ikonu manuel SVG (hafif & ölçeklenebilir) */}
            {(() => {
              const s = Math.min(minDim * 0.55, cellSize * 1.8);
              const dx = cx - s/2;
              const dy = cy - s/2;
              return (
                <g transform={`translate(${dx}, ${dy}) scale(${s/24})`}
                   stroke={info.border} strokeWidth="2" fill="none"
                   strokeLinecap="round" strokeLinejoin="round" opacity="0.9">
                  {/* lucide DoorOpen path */}
                  <path d="M13 4h3a2 2 0 0 1 2 2v14" />
                  <path d="M2 20h20" />
                  <path d="M13 20V3.5a.5.5 0 0 0-.7-.5L4 6v14" />
                  <circle cx="11" cy="13" r="0.6" fill={info.border} stroke="none" />
                </g>
              );
            })()}
          </g>
        ) : item.type === 'fire_cabinet' ? (
          /* Yangın dolabı: kompakt ikon */
          <g>
            <rect x={cx - rw*0.18} y={cy - rh*0.2} width={rw*0.36} height={rh*0.45}
              rx={rw*0.06} fill={info.border} opacity="0.9" />
            <ellipse cx={cx} cy={cy - rh*0.28} rx={rw*0.12} ry={rh*0.1}
              fill={info.border} opacity="0.9" />
            <path d={`M ${cx + rw*0.18} ${cy - rh*0.15} Q ${cx + rw*0.32} ${cy - rh*0.05} ${cx + rw*0.2} ${cy + rh*0.1}`}
              fill="none" stroke={info.border} strokeWidth={Math.max(1, rw*0.08)} opacity="0.9" />
            {minDim >= cellSize * 1.5 && (
              <text x={cx} y={cy + rh*0.42} textAnchor="middle" dominantBaseline="middle"
                fontSize={Math.max(8, minDim * 0.18)} fill={info.border} fontWeight="700" fontFamily="sans-serif">
                {t('Fire Cabinet','Yangın Dolabı')}
              </text>
            )}
          </g>
        ) : item.type === 'water' ? (
          /* Su: damla ikonu */
          <g>
            {/* Su damlası path */}
            <path
              d={`M ${cx} ${cy - rh * 0.35}
                  C ${cx + rw*0.18} ${cy - rh*0.1}, ${cx + rw*0.22} ${cy + rh*0.05}, ${cx + rw*0.22} ${cy + rh*0.12}
                  A ${rw*0.22} ${rh*0.22} 0 1 1 ${cx - rw*0.22} ${cy + rh*0.12}
                  C ${cx - rw*0.22} ${cy + rh*0.05}, ${cx - rw*0.18} ${cy - rh*0.1}, ${cx} ${cy - rh*0.35} Z`}
              fill={info.border} opacity="0.85" />
            {/* İç parlama */}
            <ellipse cx={cx - rw*0.07} cy={cy} rx={rw*0.06} ry={rh*0.09}
              fill="white" opacity="0.4" />
            {minDim >= cellSize * 1.5 && (
              <text x={cx} y={cy + rh*0.45} textAnchor="middle" dominantBaseline="middle"
                fontSize={Math.max(8, minDim * 0.18)} fill={info.border} fontWeight="700" fontFamily="sans-serif">
                {t('Water','Su')}
              </text>
            )}
          </g>
        ) : item.type === 'electricity' ? (
          /* Elektrik: şimşek ikonu */
          <g>
            <path
              d={`M ${cx + rw*0.08} ${cy - rh*0.38}
                  L ${cx - rw*0.12} ${cy + rh*0.05}
                  L ${cx + rw*0.04} ${cy + rh*0.05}
                  L ${cx - rw*0.08} ${cy + rh*0.38}
                  L ${cx + rw*0.18} ${cy - rh*0.05}
                  L ${cx + rw*0.02} ${cy - rh*0.05}
                  Z`}
              fill={info.border} opacity="0.9" />
            {minDim >= cellSize * 1.5 && (
              <text x={cx} y={cy + rh*0.52} textAnchor="middle" dominantBaseline="middle"
                fontSize={Math.max(8, minDim * 0.18)} fill={info.border} fontWeight="700" fontFamily="sans-serif">
                {t('Electricity','Elektrik')}
              </text>
            )}
          </g>
        ) : minDim >= cellSize * 1.5 && (
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
            fontSize={Math.max(10, Math.min(18, minDim * 0.22))}
            fill={info.border} fontWeight="600" fontFamily="sans-serif" opacity="0.8">
            {item.type === 'corridor' ? info.label : (info.icon + ' ' + info.label)}
          </text>
        )}

        {/* Seçim çerçevesi */}
        {isSelected && (
          <rect x={x - 2/zoom} y={y - 2/zoom} width={rw + 4/zoom} height={rh + 4/zoom}
            fill="none" stroke="#0ea5e9" strokeWidth={2/zoom} strokeDasharray={`${4/zoom},${2/zoom}`} />
        )}

        {/* Seçili → altında Sil butonu */}
        {isSelected && (
          <g style={{ cursor: 'pointer' }}
            onMouseDown={e => { e.stopPropagation(); e.preventDefault(); }}
            onMouseUp={e => {
              e.stopPropagation();
              setInfraItems(prev => {
                const next = new Map(prev);
                next.delete(item.id);
                saveInfra(next);
                return next;
              });
              setSelectedInfraId(null);
            }}>
            <rect x={cx - 22/zoom} y={y + rh + 4/zoom}
              width={44/zoom} height={16/zoom} rx={3/zoom} fill="#ef4444" />
            <text x={cx} y={y + rh + 12/zoom}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={9/zoom} fill="#fff" fontWeight="bold"
              style={{ userSelect: 'none' }} pointerEvents="none">
              Sil
            </text>
          </g>
        )}
      </g>
    );
  };

  /* ── Drag preview rectangle ───────────────────────────────────────────── */
  const renderDragPreview = () => {
    if (!isDragging || !dragStart || !dragEnd) return null;
    if (activeTool === 'eraser' || interactMode) return null;
    const isStand = activeTool === 'stand';
    const minCol = Math.min(dragStart.col, dragEnd.col);
    const maxCol = Math.max(dragStart.col, dragEnd.col);
    const minRow = Math.min(dragStart.row, dragEnd.row);
    const maxRow = Math.max(dragStart.row, dragEnd.row);
    // Stand: köşe modeli (span = fark). Infra: hücre-inclusive (span = fark + 1).
    const spanC = isStand ? (maxCol - minCol) : (maxCol - minCol + 1);
    const spanR = isStand ? (maxRow - minRow) : (maxRow - minRow + 1);
    const w  = spanC * cellSize;
    const h  = spanR * cellSize;
    const x  = minCol * cellSize;
    const y  = minRow * cellSize;
    const wM = cellsToM(spanC);
    const hM = cellsToM(spanR);

    // Color depends on tool
    const infraType = INFRA_TYPES[activeTool];
    const fillClr   = isStand ? 'rgba(124,58,237,0.18)'   : (infraType?.fill   || 'rgba(148,163,184,0.35)');
    const strokeClr = isStand ? '#7c3aed'                  : (infraType?.border || '#94a3b8');
    const labelBg   = isStand ? '#7c3aed'                  : (infraType?.border || '#64748b');

    return (
      <g style={{ pointerEvents: 'none' }}>
        <rect x={x} y={y} width={w} height={h}
          fill={fillClr}
          stroke={strokeClr}
          strokeWidth={1.5 / zoom}
          strokeDasharray={`${4/zoom} ${3/zoom}`} />
        {/* Dimension + tool label */}
        <rect
          x={x + w/2 - (36/zoom)}
          y={y - 16/zoom}
          width={72/zoom} height={14/zoom}
          fill={labelBg} rx={3/zoom} />
        <text
          x={x + w/2} y={y - 5/zoom}
          textAnchor="middle" fontSize={9/zoom}
          fill="white" fontWeight="bold" fontFamily="sans-serif">
          {isStand ? `${wM}m × ${hM}m` : `${(language === 'en' ? infraType?.labelEn : infraType?.label) || ''} ${wM}×${hM}m`}
        </text>
      </g>
    );
  };

  /* ── Resize handle mousedown ──────────────────────────────────────────── */
  const onResizeHandleDown = (e, handle) => {
    e.stopPropagation();
    if (!selectedStand) return;
    const cell = svgToCell(e);
    if (!cell) return;
    const cells = selectedStand.cells || [];
    const cols  = cells.map(c => c.col);
    const rows  = cells.map(c => c.row);
    setInteractMode('resize');
    setInteractStart({
      cell,
      handle,
      originalCells:  cells.map(c => ({ ...c })),
      originalBounds: {
        minC: Math.min(...cols), maxC: Math.max(...cols),
        minR: Math.min(...rows), maxR: Math.max(...rows),
      },
    });
    setIsDragging(true);
  };

  /* ── Modal size change (W/H input) ────────────────────────────────────── */
  const handleModalSizeChange = (newW, newH) => {
    const cells = editingStand ? (editingStand.cells || []) : pendingCells;
    if (!cells.length) return;
    const minC = Math.min(...cells.map(c => c.col));
    const minR = Math.min(...cells.map(c => c.row));
    const w = Math.max(1, Math.min(newW, hall?.width  || 200));
    const h = Math.max(1, Math.min(newH, hall?.height || 200));
    const newCells = [];
    for (let c = minC; c < minC + w; c++)
      for (let r = minR; r < minR + h; r++)
        newCells.push({ col: c, row: r });
    if (editingStand) {
      setEditingStand(prev => ({ ...prev, cells: newCells }));
    } else {
      setPendingCells(newCells);
    }
  };

  /* ── Render SVG resize handles ────────────────────────────────────────── */
  const renderResizeHandles = () => {
    if (!selectedStand || activeTool !== 'stand') return null;
    const cells = selectedStand.cells || [];
    if (!cells.length) return null;
    const cols = cells.map(c => c.col), rows = cells.map(c => c.row);
    const minC = Math.min(...cols), maxC = Math.max(...cols);
    const minR = Math.min(...rows), maxR = Math.max(...rows);
    const x0 = minC * cellSize, y0 = minR * cellSize;
    const x1 = (maxC + 1) * cellSize, y1 = (maxR + 1) * cellSize;
    const hw = Math.max(3, 5 / zoom); // half-width: stays visually ~10px

    const handles = [
      { id: 'nw', cx: x0,          cy: y0,          cursor: 'nw-resize' },
      { id: 'ne', cx: x1,          cy: y0,          cursor: 'ne-resize' },
      { id: 'sw', cx: x0,          cy: y1,          cursor: 'sw-resize' },
      { id: 'se', cx: x1,          cy: y1,          cursor: 'se-resize' },
      { id: 'n',  cx: (x0+x1)/2,   cy: y0,          cursor: 'n-resize'  },
      { id: 's',  cx: (x0+x1)/2,   cy: y1,          cursor: 's-resize'  },
      { id: 'w',  cx: x0,          cy: (y0+y1)/2,   cursor: 'w-resize'  },
      { id: 'e',  cx: x1,          cy: (y0+y1)/2,   cursor: 'e-resize'  },
    ];

    return (
      <g>
        {handles.map(h => (
          <rect key={h.id}
            x={h.cx - hw} y={h.cy - hw}
            width={hw * 2} height={hw * 2}
            fill="white" stroke="#7c3aed"
            strokeWidth={1.5 / zoom}
            rx={1.5 / zoom}
            style={{ cursor: h.cursor }}
            onMouseDown={e => onResizeHandleDown(e, h.id)} />
        ))}
      </g>
    );
  };

  /* ── Render move/resize live preview ──────────────────────────────────── */
  const renderPreviewLayer = () => {
    if (!previewCells || !selectedStand) return null;
    const cols = previewCells.map(c => c.col), rows = previewCells.map(c => c.row);
    const minC = Math.min(...cols), maxC = Math.max(...cols);
    const minR = Math.min(...rows), maxR = Math.max(...rows);
    return (
      <g style={{ pointerEvents: 'none' }}>
        {previewCells.map(c => (
          <rect key={`pv-${c.col}-${c.row}`}
            x={c.col * cellSize} y={c.row * cellSize}
            width={cellSize} height={cellSize}
            fill={hexToRgba(getStandBorder(selectedStand), 0.45)} />
        ))}
        <rect
          x={minC * cellSize} y={minR * cellSize}
          width={(maxC - minC + 1) * cellSize}
          height={(maxR - minR + 1) * cellSize}
          fill="none"
          stroke={getStandBorder(selectedStand)}
          strokeWidth={2 / zoom}
          strokeDasharray={`${6 / zoom} ${3 / zoom}`} />
        {/* Dimension label on preview */}
        <rect
          x={(minC + (maxC - minC + 1) / 2) * cellSize - 28 / zoom}
          y={minR * cellSize - 15 / zoom}
          width={56 / zoom} height={12 / zoom}
          fill={getStandBorder(selectedStand)} rx={3 / zoom} />
        <text
          x={(minC + (maxC - minC + 1) / 2) * cellSize}
          y={minR * cellSize - 5 / zoom}
          textAnchor="middle" fontSize={8 / zoom}
          fill="white" fontWeight="bold" fontFamily="sans-serif">
          {cellsToM(maxC - minC + 1)}m × {cellsToM(maxR - minR + 1)}m
        </text>
      </g>
    );
  };

  /* ── Floating HTML toolbar (overlay above selected stand) ─────────────── */
  const renderFloatingToolbar = () => {
    if (!selectedStand) return null;
    const cells = selectedStand.cells || [];
    if (!cells.length) return null;
    const cols = cells.map(c => c.col), rows = cells.map(c => c.row);
    const minC = Math.min(...cols), maxC = Math.max(...cols), minR = Math.min(...rows);
    const cx = (minC + (maxC - minC + 1) / 2) * cellSize * zoom;
    const ty = minR * cellSize * zoom;

    const btnBase = {
      background: 'transparent', border: 'none', cursor: 'pointer',
      padding: '4px 9px', borderRadius: '6px',
      display: 'flex', alignItems: 'center', gap: '5px',
      fontSize: '11px', fontWeight: '500', whiteSpace: 'nowrap',
      transition: 'background 0.12s',
    };

    return (
      <div
        data-testid="stand-floating-toolbar"
        onMouseDown={e => e.stopPropagation()}
        style={{
          position: 'absolute',
          left:  `${cx}px`,
          top:   `${ty}px`,
          transform: 'translate(-50%, calc(-100% - 10px))',
          zIndex: 30,
          userSelect: 'none',
        }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '1px',
          background: '#111827', borderRadius: '10px',
          padding: '3px 4px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)',
        }}>
          <button
            title="Düzenle" onClick={() => guardedRun(selectedStand, 'update', () => openEditModal(selectedStand), 'düzenle', 'edit')}
            disabled={!ownershipFor(selectedStand, 'update').allowed}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            style={{ ...btnBase, color: ownershipFor(selectedStand, 'update').allowed ? '#e2e8f0' : '#64748b', opacity: ownershipFor(selectedStand, 'update').allowed ? 1 : 0.45, cursor: ownershipFor(selectedStand, 'update').allowed ? 'pointer' : 'not-allowed' }}>
            <Pencil size={12} /><span>Düzenle</span>
          </button>
          <button
            title="Kopyala" onClick={() => copyStand(selectedStand)}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            style={{ ...btnBase, color: '#e2e8f0' }}>
            <Copy size={12} /><span>Kopyala</span>
          </button>
          <button
            title="E-posta" onClick={() => setShowEmailDialog(true)}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            style={{ ...btnBase, color: '#e2e8f0' }}>
            <Mail size={12} /><span>E-posta</span>
          </button>
          <div style={{ width: '1px', height: '18px', background: '#374151', margin: '0 3px' }} />
          <button
            title="Sil" onClick={() => guardedRun(selectedStand, 'delete', () => deleteStand(selectedStand.id), 'sil', 'delete')}
            disabled={!ownershipFor(selectedStand, 'delete').allowed}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            style={{ ...btnBase, color: ownershipFor(selectedStand, 'delete').allowed ? '#fca5a5' : '#64748b', opacity: ownershipFor(selectedStand, 'delete').allowed ? 1 : 0.45, cursor: ownershipFor(selectedStand, 'delete').allowed ? 'pointer' : 'not-allowed' }}>
            <Trash2 size={12} /><span>Sil</span>
          </button>
        </div>
        {/* Arrow pointing down */}
        <div style={{
          position: 'absolute', bottom: '-5px', left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderTop: '5px solid #111827',
          width: 0, height: 0,
        }} />
      </div>
    );
  };

  /* ── Auto-detect adjacent stand sides (shared edges = closed) ───────────── */
  const getAutoClosedSides = (stand) => {
    if (!stand.cells || stand.cells.length === 0) return new Set();
    const cols = stand.cells.map(c => c.col);
    const rows = stand.cells.map(c => c.row);
    const minCol = Math.min(...cols), maxCol = Math.max(...cols);
    const minRow = Math.min(...rows), maxRow = Math.max(...rows);
    const auto = new Set();
    for (const other of stands) {
      if (other.id === stand.id || !other.cells || other.cells.length === 0) continue;
      const oc = other.cells.map(c => c.col);
      const or_ = other.cells.map(c => c.row);
      const omn = Math.min(...oc), omx = Math.max(...oc);
      const rmn = Math.min(...or_), rmx = Math.max(...or_);
      const rowOlap = minRow <= rmx && rmn <= maxRow;
      const colOlap = minCol <= omx && omn <= maxCol;
      if (maxCol + 1 === omn && rowOlap) auto.add('east');
      if (omx + 1 === minCol && rowOlap) auto.add('west');
      if (maxRow + 1 === rmn && colOlap) auto.add('south');
      if (rmx + 1 === minRow && colOlap) auto.add('north');
    }
    return auto;
  };
  const STATUS_BTN = {
    available:   { bg: gridSettings.available_color,   label: t('Available','Müsait')   },
    reserved:    { bg: gridSettings.reserved_color,    label: t('Reserved','Rezerve')   },
    unavailable: { bg: gridSettings.unavailable_color, label: t('Sold','Satıldı')       },
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 flex items-center justify-center text-gray-400">Yükleniyor...</div>
    </div>
  );
  if (!hall) return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 flex items-center justify-center text-gray-400">Hall bulunamadı.</div>
    </div>
  );

  const filteredTenants = tenants.filter(t =>
    t.name.toLowerCase().includes(tenantSearch.toLowerCase())
  );

  /* ════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />

      <div className="flex-1 flex flex-col overflow-hidden">

        {/* ── Top bar ─────────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-200 px-5 py-2 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/ultra-admin/halls')}
              className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-bold text-gray-800">{hall.name}</h1>
              <p className="text-xs text-gray-400">
                {cellsToM(hall.width)}m × {cellsToM(hall.height)}m · {stands.length} {t('stands','stand')} · {stands.filter(s => s.tenant_id).length} {t('assigned','atanmış')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom */}
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-xl px-2 py-1">
              <button onClick={() => setZoom(v => Math.max(MIN_ZOOM, parseFloat((v-0.15).toFixed(2))))}
                className="p-1 hover:bg-white rounded-lg transition-colors" title="Uzaklaştır">
                <ZoomOut className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => setZoom(1.0)}
                className="px-2 text-xs font-bold text-gray-600 hover:bg-white rounded-lg py-0.5 min-w-[50px] text-center"
                title="100% sıfırla">
                {Math.round(zoom * 100)}%
              </button>
              <button onClick={() => setZoom(v => Math.min(MAX_ZOOM, parseFloat((v+0.15).toFixed(2))))}
                className="p-1 hover:bg-white rounded-lg transition-colors" title="Yakınlaştır">
                <ZoomIn className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => {
                  const fitW = (780 - OFFSET) / (hall.width  * cellSize);
                  const fitH = (540 - OFFSET) / (hall.height * cellSize);
                  setZoom(parseFloat(Math.min(fitW, fitH, MAX_ZOOM).toFixed(2)));
                }}
                className="p-1 hover:bg-white rounded-lg transition-colors" title="Sığdır">
                <Maximize2 className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Undo/Redo */}
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-xl px-1 py-1">
              <button onClick={undoInfra} disabled={!histState.canUndo}
                className="p-1 hover:bg-white rounded-lg disabled:opacity-30" title="Geri Al">
                <Undo2 className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={redoInfra} disabled={!histState.canRedo}
                className="p-1 hover:bg-white rounded-lg disabled:opacity-30" title="İleri Al">
                <Redo2 className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <button onClick={exportPNG}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
              <Download className="w-4 h-4" /> PNG
            </button>
            <button data-testid="fullscreen-open-btn" onClick={() => {
                setIsFullscreen(true);
                // Tam ekrana geçince planı ekrana sığdır (fit-to-screen)
                setTimeout(() => {
                  if (!hall) return;
                  const fitW = (window.innerWidth - 40) / (hall.width * cellSize);
                  const fitH = (window.innerHeight - 110) / (hall.height * cellSize);
                  setZoom(parseFloat(Math.max(MIN_ZOOM, Math.min(fitW, fitH, MAX_ZOOM)).toFixed(2)));
                }, 50);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
              title={t('Full screen plan','Tam ekran plan')}>
              <Maximize2 className="w-4 h-4" /> {t('Full screen','Tam Ekran')}
            </button>
            <LanguageToggle />
            <button onClick={() => { localStorage.clear(); navigate('/giris'); }}
              className="text-gray-400 hover:text-red-500 p-1">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── Tool Toolbar ─────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-100 px-4 py-2 flex items-center gap-1.5 flex-wrap flex-shrink-0">
          {TOOL_BUTTONS.map(tool => {
            const isActive = activeTool === tool.id;
            const IconComp = tool.Icon;
            return (
              <React.Fragment key={tool.id}>
                <button data-testid={`tool-${tool.id}`}
                  onClick={() => setActiveTool(tool.id)}
                  title={tool.label}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all border ${
                    isActive ? 'shadow-sm text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                  style={isActive ? { background: tool.color, borderColor: tool.color } : {}}>
                  <IconComp className="h-4 w-4" />
                  <span>{tl(tool)}</span>
                </button>
                {/* Kolon butonu → Yangın Dolabı'nın hemen yanında */}
                {tool.id === 'fire_cabinet' && (
                  <button data-testid="tool-column"
                    onClick={() => setColumnModal(true)}
                    title="Kolon"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all border bg-white border-gray-200 text-gray-600 hover:border-slate-400 hover:bg-slate-50">
                    <Columns3 className="h-4 w-4" />
                    <span>{t('Column','Kolon')}</span>
                  </button>
                )}
              </React.Fragment>
            );
          })}
          {/* Pan butonu */}
          <button data-testid="tool-pan"
            onClick={() => setActiveTool('pan')}
            title="Pan (Kaydır)"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all border ${
              activeTool === 'pan' ? 'text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-slate-400 hover:bg-slate-50'
            }`}
            style={activeTool === 'pan' ? { background: '#64748b', borderColor: '#64748b' } : {}}>
            <Hand className="h-4 w-4" />
            <span>{t('Pan','Pan')}</span>
          </button>

          {/* ── Annotation tools ──────────────────────────────────────────── */}
          <div className="w-px h-6 bg-gray-200 mx-1" />
          {ANNOTATION_TOOLS.map(tool => {
            const isActive = activeTool === tool.id;
            const IconComp = tool.Icon;
            return (
              <button key={tool.id} data-testid={`tool-${tool.id}`}
                onClick={() => { setActiveTool(tool.id); setSelectedAnnId(null); setSelectedStand(null); setSelectedColId(null); setSelectedInfraId(null); }}
                title={tl(tool)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all border ${
                  isActive ? 'text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-cyan-300'
                }`}
                style={isActive ? { background: tool.color, borderColor: tool.color } : {}}>
                <IconComp className="h-4 w-4" />
                <span>{tl(tool)}</span>
              </button>
            );
          })}
          <button data-testid="tool-logo"
            onClick={() => logoInputRef.current?.click()}
            disabled={uploadingLogo}
            title={t('Upload logo/image','Logo/görsel yükle')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all border bg-white border-gray-200 text-gray-600 hover:border-cyan-300 disabled:opacity-50">
            <ImageIcon className="h-4 w-4" />
            <span>{uploadingLogo ? t('Uploading…','Yükleniyor…') : t('Logo','Logo')}</span>
          </button>
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            data-testid="logo-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ''; }} />

          <button data-testid="tool-background"
            onClick={() => bgInputRef.current?.click()}
            disabled={uploadingBg}
            title={t('Upload background image','Arka plan görseli yükle')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all border bg-white border-gray-200 text-gray-600 hover:border-indigo-300 disabled:opacity-50">
            <LayoutGrid className="h-4 w-4" />
            <span>{uploadingBg ? t('Uploading…','Yükleniyor…') : t('Background','Arka Plan')}</span>
          </button>
          <input ref={bgInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            data-testid="background-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadBackground(f); e.target.value = ''; }} />
          {hall?.background && (
            <button data-testid="tool-background-remove"
              onClick={removeBackground}
              title={t('Remove background','Arka planı kaldır')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all border bg-white border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
              <span>{t('Remove BG','Arka Planı Kaldır')}</span>
            </button>
          )}

          {/* Colour + font + delete for annotations */}
          {(() => {
            const selAnn = annotations.find(a => a.id === selectedAnnId);
            const showText = activeTool === 'ann_text' || selAnn?.type === 'text';
            const showColour = ['measure'].indexOf(activeTool) === -1 &&
              (ANNOTATION_TOOLS.some(x => x.id === activeTool) || (selAnn && selAnn.type !== 'image' && selAnn.type !== 'measure'));
            return (
              <>
                {showColour && (
                  <input type="color" data-testid="ann-color"
                    value={selAnn && selAnn.color ? selAnn.color : annStyle.color}
                    onChange={(e) => { const c = e.target.value; setAnnStyle(s => ({ ...s, color: c })); if (selAnn) updateAnnotation(selAnn.id, { color: c }); }}
                    title={t('Colour','Renk')}
                    className="w-8 h-8 rounded-lg border border-gray-200 cursor-pointer p-0.5" />
                )}
                {showText && (
                  <>
                    <select data-testid="ann-font"
                      value={selAnn?.fontFamily || annStyle.fontFamily}
                      onChange={(e) => { const v = e.target.value; setAnnStyle(s => ({ ...s, fontFamily: v })); if (selAnn) updateAnnotation(selAnn.id, { fontFamily: v }); }}
                      className="text-sm border border-gray-200 rounded-lg px-2 py-1.5">
                      {FONT_FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                      <button data-testid="ann-font-dec" className="px-2 py-1 hover:bg-gray-100 text-gray-600"
                        onClick={() => { const nv = Math.max(8, (selAnn?.fontSize || annStyle.fontSize) - 2); setAnnStyle(s => ({ ...s, fontSize: nv })); if (selAnn) updateAnnotation(selAnn.id, { fontSize: nv }); }}>A-</button>
                      <span className="px-1.5 text-xs text-gray-500 tabular-nums">{selAnn?.fontSize || annStyle.fontSize}</span>
                      <button data-testid="ann-font-inc" className="px-2 py-1 hover:bg-gray-100 text-gray-600"
                        onClick={() => { const nv = Math.min(120, (selAnn?.fontSize || annStyle.fontSize) + 2); setAnnStyle(s => ({ ...s, fontSize: nv })); if (selAnn) updateAnnotation(selAnn.id, { fontSize: nv }); }}>A+</button>
                    </div>
                  </>
                )}
                {selAnn && (
                  <button data-testid="ann-delete"
                    onClick={() => deleteAnnotation(selAnn.id)}
                    title={t('Delete selected','Seçileni sil')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                    <span>{t('Delete','Sil')}</span>
                  </button>
                )}
              </>
            );
          })()}

          <div className="flex-1" />
          <span className="text-xs text-gray-400 hidden md:block">
            {activeTool === 'stand'
              ? t('Drag → rectangle stand · Click → select','Sürükle → dikdörtgen stand · Tıkla → seç')
              : activeTool === 'eraser'
              ? t('Erase infrastructure marks','Altyapı işaretlerini sil')
              : activeTool === 'pan'
              ? t('Click and drag → pan the canvas','Tıkla ve sürükle → canvas\'ı kaydır')
              : activeTool === 'fire_cabinet'
              ? t('Click → place 1×1m fire cabinet','Tıkla → 1×1m yangın dolabı yerleştir')
              : activeTool === 'water'
              ? t('Click → place 1×1m water point','Tıkla → 1×1m su bağlantısı yerleştir')
              : activeTool === 'electricity'
              ? t('Click → place 1×1m electricity point','Tıkla → 1×1m elektrik bağlantısı yerleştir')
              : `${tl(TOOL_BUTTONS.find(b=>b.id===activeTool)||{label:'',labelEn:''})} — ${t('drag to draw rectangle','sürükle/bırak ile dikdörtgen alan çiz')}`}
            &nbsp;· {t('Wheel: zoom','Tekerlek: zoom')}
          </span>
        </div>

        {/* ── Hint bar ─────────────────────────────────────────────────────── */}
        {activeTool !== 'stand' && activeTool !== 'eraser' && activeTool !== 'pan' && activeTool !== 'fire_cabinet' && activeTool !== 'water' && activeTool !== 'electricity' && (
          <div className="border-b px-5 py-1 text-xs font-medium flex-shrink-0"
            style={{ background: INFRA_TYPES[activeTool]?.fill || 'rgba(200,200,200,0.2)', borderColor: INFRA_TYPES[activeTool]?.border || '#e5e7eb' }}>
            <strong style={{ color: INFRA_TYPES[activeTool]?.border }}>{tl(TOOL_BUTTONS.find(b=>b.id===activeTool)||INFRA_TYPES[activeTool]||{label:'',labelEn:''})}</strong> {t('mode','modu')}
          </div>
        )}

        {/* ── Main area ────────────────────────────────────────────────────── */}
        <div className="flex-1 flex overflow-hidden p-4 gap-4">

          {/* Canvas */}
          <div className={`flex-1 flex flex-col min-w-0 ${isFullscreen ? 'fixed inset-0 z-[90] bg-white p-3' : ''}`}>
            {isFullscreen && (
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <h3 className="text-sm font-bold text-gray-700 truncate">{hall.name}</h3>
                <div className="flex items-center gap-0.5 bg-gray-100 rounded-xl px-1 py-1">
                  <button onClick={() => setZoom(v => Math.max(MIN_ZOOM, parseFloat((v - 0.15).toFixed(2))))}
                    className="p-1 hover:bg-white rounded-lg" title="Uzaklaştır"><ZoomOut className="w-4 h-4 text-gray-600" /></button>
                  <button onClick={() => setZoom(1.0)}
                    className="px-2 text-xs font-bold text-gray-600 min-w-[50px] text-center">{Math.round(zoom * 100)}%</button>
                  <button onClick={() => setZoom(v => Math.min(MAX_ZOOM, parseFloat((v + 0.15).toFixed(2))))}
                    className="p-1 hover:bg-white rounded-lg" title="Yakınlaştır"><ZoomIn className="w-4 h-4 text-gray-600" /></button>
                  <button onClick={() => { const fitW = (window.innerWidth - 40) / (hall.width * cellSize); const fitH = (window.innerHeight - 120) / (hall.height * cellSize); setZoom(parseFloat(Math.min(fitW, fitH, MAX_ZOOM).toFixed(2))); }}
                    className="p-1 hover:bg-white rounded-lg" title="Sığdır"><Maximize2 className="w-4 h-4 text-gray-600" /></button>
                  <button data-testid="fullscreen-close-btn" onClick={() => setIsFullscreen(false)}
                    className="p-1 hover:bg-red-50 rounded-lg ml-1" title={t('Close','Kapat')}><X className="w-4 h-4 text-red-500" /></button>
                </div>
              </div>
            )}
            <div ref={containerRef}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-auto flex-1"
              style={{ maxHeight: isFullscreen ? 'calc(100vh - 80px)' : 'calc(100vh - 185px)', position: 'relative', cursor: interactMode === 'move' ? 'grabbing' : activeTool === 'pan' ? (panRef.current ? 'grabbing' : 'grab') : activeTool === 'stand' ? 'crosshair' : activeTool === 'eraser' ? 'cell' : 'crosshair' }}>
              <svg ref={svgRef}
                width={baseSvgW * zoom} height={baseSvgH * zoom}
                style={{ display: 'block' }}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}>
                <g transform={`scale(${zoom})`}>

                  {/* 1 ─ Hall background */}
                  <rect data-hall-bgrect="1" x={0} y={0}
                    width={hall.width * cellSize} height={hall.height * cellSize}
                    fill="#f8fafc" />

                  {/* 1b ─ Kilitli arka plan görseli (floor-plan) — en-boy oranı korunur, seçilemez/taşınamaz */}
                  {hall.background?.url && (
                    <image
                      data-hall-bg="1"
                      href={`${process.env.REACT_APP_BACKEND_URL}${hall.background.url}`}
                      x={0} y={0}
                      width={hall.width * cellSize} height={hall.height * cellSize}
                      preserveAspectRatio="xMidYMid meet"
                      style={{ pointerEvents: 'none' }} />
                  )}

                  {/* 2 ─ Infrastructure layer — dikdörtgen bazlı render (performanslı) */}
                  {Array.from(infraItems.values()).map(item => renderInfraRect(item))}

                  {/* ── Columns / Pillars ─────────────────────────────── */}
                  {columns.map(col => {
                    const cx  = (col.col + (col.shape === 'daire' ? col.d / 2 : col.w / 2)) * cellSize;
                    const cy  = (col.row + (col.shape === 'daire' ? col.d / 2 : col.h / 2)) * cellSize;
                    const cw  = (col.shape === 'daire' ? col.d : col.w) * cellSize;
                    const ch  = (col.shape === 'daire' ? col.d : col.h) * cellSize;
                    const rx  = col.col * cellSize;
                    const ry  = col.row * cellSize;
                    const isDraggingThis = draggingCol?.id === col.id;
                    const isSelected     = selectedColId === col.id;
                    const fill = isDraggingThis ? '#94a3b8' : isSelected ? '#0ea5e9' : '#475569';
                    const strokeClr = isSelected ? '#0369a1' : '#1e293b';
                    return (
                      <g key={col.id}
                        style={{ cursor: 'grab' }}
                        onMouseDown={e => {
                          e.stopPropagation();
                          // Seçim toggle
                          setSelectedColId(prev => prev === col.id ? null : col.id);
                          // Drag başlat
                          const cell = svgToCell(e);
                          if (!cell) return;
                          const offC = cell.col - col.col;
                          const offR = cell.row - col.row;
                          draggingColRef.current = { id: col.id, offC, offR };
                          setDraggingCol({ id: col.id });
                        }}>
                        {col.shape === 'daire'
                          ? <circle cx={cx} cy={cy} r={cw / 2} fill={fill} stroke={strokeClr} strokeWidth={isSelected ? 2/zoom : borderStrokeW} />
                          : <rect x={rx} y={ry} width={cw} height={ch} rx={2} fill={fill} stroke={strokeClr} strokeWidth={isSelected ? 2/zoom : borderStrokeW} />
                        }
                        {/* Label */}
                        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
                          fontSize={Math.max(6, Math.min(14, cellSize * 0.5))} fill="#fff" fontWeight="700" pointerEvents="none">
                          K
                        </text>
                        {/* Seçili ise → kolonun ALTINDA küçük silme butonu (sürüklemeye engel olmaz) */}
                        {isSelected && (
                          <g
                            style={{ cursor: 'pointer' }}
                            onMouseDown={e => { e.stopPropagation(); e.preventDefault(); }}
                            onMouseUp={e => {
                              e.stopPropagation();
                              deleteColumn(col.id);
                              setSelectedColId(null);
                            }}>
                            <rect
                              x={cx - 22/zoom} y={cy + ch/2 + 4/zoom}
                              width={44/zoom} height={16/zoom}
                              rx={3/zoom} fill="#ef4444" />
                            <text
                              x={cx} y={cy + ch/2 + 12/zoom}
                              textAnchor="middle" dominantBaseline="middle"
                              fontSize={9/zoom} fill="#fff" fontWeight="bold"
                              style={{ userSelect: 'none' }} pointerEvents="none">
                              Sil
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })}

                  {/* 3 ─ Stand areas — SOLID fill, no cell gaps */}
                  {stands.map(stand => {
                    const cells  = stand.cells || [];
                    const fill   = getStandFill(stand);
                    const isSelected = selectedStand?.id === stand.id;
                    const dimmed     = isSelected && previewCells !== null;
                    return cells.map(c => (
                      <rect key={`${stand.id}-${c.col}-${c.row}`}
                        x={c.col * cellSize}
                        y={c.row * cellSize}
                        width={cellSize}
                        height={cellSize}
                        fill={fill}
                        opacity={dimmed ? 0.25 : 1}
                        style={{ cursor: isSelected && activeTool === 'stand' && !interactMode ? 'grab' : 'inherit' }} />
                    ));
                  })}

                  {/* 4 ─ Grid lines — HER ZAMAN 1 m aralık (0.5 m hücre hassasiyetinden bağımsız). Arka plan olsa da görünür kalır. */}
                  {hall && (() => {
                    const lines = [];
                    const gStroke = 0.5 / zoom;
                    const step = Math.max(1, Math.round(1 / gridUnit)); // 1 metredeki hücre sayısı
                    // Dikey çizgiler
                    for (let c = step; c < hall.width; c += step) {
                      lines.push(<line key={`v${c}`}
                        x1={c * cellSize} y1={0}
                        x2={c * cellSize} y2={hall.height * cellSize}
                        stroke="#cbd5e1" strokeWidth={gStroke} />);
                    }
                    // Yatay çizgiler
                    for (let r = step; r < hall.height; r += step) {
                      lines.push(<line key={`h${r}`}
                        x1={0} y1={r * cellSize}
                        x2={hall.width * cellSize} y2={r * cellSize}
                        stroke="#cbd5e1" strokeWidth={gStroke} />);
                    }
                    return lines;
                  })()}

                  {/* 5 ─ Hall border (on top of grid) */}
                  <rect x={0} y={0}
                    width={hall.width * cellSize} height={hall.height * cellSize}
                    fill="none" stroke="#94a3b8" strokeWidth={borderStrokeW} />

                  {/* 6 ─ Stand labels (SS2 style) */}
                  {stands.map(stand => {
                    const cells = stand.cells || [];
                    if (!cells.length) return null;
                    const cols = cells.map(c => c.col), rows = cells.map(c => c.row);
                    const minCol2 = Math.min(...cols), maxCol2 = Math.max(...cols);
                    const minRow2 = Math.min(...rows), maxRow2 = Math.max(...rows);
                    const x0  = minCol2 * cellSize;
                    const y0  = minRow2 * cellSize;
                    const x1  = (maxCol2 + 1) * cellSize;
                    const y1  = (maxRow2 + 1) * cellSize;
                    const cx  = (x0 + x1) / 2;
                    const cy  = (y0 + y1) / 2;
                    const border = getStandBorder(stand);
                    // Müşteri/katılımcı seçiliyse onun adı, değilse Stand Adı gösterilir.
                    const displayName = stand.tenant_name || stand.stand_name || '';
                    const showArea   = !!stand.show_area;      // eşik kaldırıldı → %100'de de görünür
                    const isSelected = selectedStand?.id === stand.id;
                    // Always dark text with white halo for readability on any background
                    const FONT_CLR = '#0f172a';
                    const HALO_CLR = 'rgba(255,255,255,0.85)';
                    // Closed sides = manually closed OR auto-detected adjacent stands
                    const autoAdj   = getAutoClosedSides(stand);
                    const openSides = stand.open_sides ?? ['north','south','east','west'];
                    const openSet   = new Set(openSides.filter(s => !autoAdj.has(s)));
                    const CLOSED_SW = 4 / zoom;
                    const CLOSED_CLR = '#111827';

                    // Text wrap for tenant name.
                    //
                    // Sizing rules (tight stands like 11m × 3m must stay readable
                    // without overflowing top/bottom):
                    //   1. Start from `standFontSz * 1.4` like before.
                    //   2. Clamp by stand HEIGHT so the rendered block (flag +
                    //      lines + bottom m²) cannot exceed ~70% of the cell.
                    //   3. Re-wrap with the clamped font so horizontal width
                    //      stays inside the rect minus a small inner padding.
                    const standW = x1 - x0;
                    const standH = y1 - y0;
                    const flagGlyph = stand.tenant_country ? countryToFlag(stand.tenant_country) : '';
                    const hasTenant = !!displayName;
                    const hasFlag   = hasTenant && flagGlyph !== '';
                    const showNo    = !!stand.stand_no;
                    const showAreaLbl = showArea;

                    // --- Birleşik, ORTALANMIŞ etiket bloğu (SS1 sade stili + stand no) ---
                    // Sıra (üstten alta): stand no → şirket adı → bayrak → m².
                    // Fontlar stand boyutuna göre ölçeklenir ama makul kalır (SS1 görünümü);
                    // blok stand yüksekliğine sığana kadar küçültülür → üst üste binme olmaz.
                    const noRatio   = hasTenant ? 0.66 : 1.0;
                    const areaRatio = hasTenant ? 0.66 : 0.72;
                    const flagRatio = 1.05;
                    const lineGap   = 1.22;
                    const CHAR_W    = 0.55; // yaklaşık karakter genişliği (bold değil → biraz dar)
                    const areaText  = `${cellsToArea(cells.length)}m²`;
                    let primary = Math.max(standFontSz, Math.min(standW * 0.19, standH * 0.15));
                    let nameLines = hasTenant ? wrapText(displayName) : [];
                    const measureBlockH = (p) => {
                      let h = 0;
                      if (showNo)      h += p * noRatio * lineGap;
                      if (hasTenant)   h += nameLines.length * p * lineGap;
                      if (hasFlag)     h += p * flagRatio * lineGap;
                      if (showAreaLbl) h += p * areaRatio * lineGap;
                      return h;
                    };
                    // En geniş satır stand'a sığıyor mu? (stand_no ve m² sarmalanmaz)
                    const fitsWidth = (p) => {
                      let w = 0;
                      if (showNo)      w = Math.max(w, (stand.stand_no || '').length * p * noRatio * CHAR_W);
                      if (hasTenant)   nameLines.forEach(ln => { w = Math.max(w, ln.length * p * CHAR_W); });
                      if (showAreaLbl) w = Math.max(w, areaText.length * p * areaRatio * CHAR_W);
                      return w <= standW * 0.92;
                    };
                    // Blok hem YÜKSEKLİĞE hem GENİŞLİĞE sığana kadar fontu küçült (kelime bölmeden)
                    for (let g = 0; g < 18; g++) {
                      if ((measureBlockH(primary) <= standH * 0.92 && fitsWidth(primary)) || primary <= standFontSz * 0.28) break;
                      primary *= 0.88;
                    }
                    const _items = [];
                    if (showNo)      _items.push({ kind: 'no',   text: stand.stand_no, font: primary * noRatio });
                    if (hasTenant)   nameLines.forEach(ln => _items.push({ kind: 'name', text: ln, font: primary }));
                    if (hasFlag)     _items.push({ kind: 'flag', text: flagGlyph, font: primary * flagRatio });
                    if (showAreaLbl) _items.push({ kind: 'area', text: areaText, font: primary * areaRatio });
                    const totalH = _items.reduce((s, it) => s + it.font * lineGap, 0);
                    let _cursorY = cy - totalH / 2;
                    const placedLabels = _items.map(it => {
                      const adv = it.font * lineGap;
                      const centerY = _cursorY + adv / 2;
                      _cursorY += adv;
                      return { ...it, centerY };
                    });

                    return (
                      <g key={`lbl-${stand.id}`} style={{ pointerEvents: 'none' }}>
                        {/* Closed-side thick borders (manual + auto adjacent) */}
                        {!openSet.has('north') && (
                          <line x1={x0} y1={y0} x2={x1} y2={y0}
                            stroke={CLOSED_CLR} strokeWidth={CLOSED_SW} strokeLinecap="square" />
                        )}
                        {!openSet.has('south') && (
                          <line x1={x0} y1={y1} x2={x1} y2={y1}
                            stroke={CLOSED_CLR} strokeWidth={CLOSED_SW} strokeLinecap="square" />
                        )}
                        {!openSet.has('east') && (
                          <line x1={x1} y1={y0} x2={x1} y2={y1}
                            stroke={CLOSED_CLR} strokeWidth={CLOSED_SW} strokeLinecap="square" />
                        )}
                        {!openSet.has('west') && (
                          <line x1={x0} y1={y0} x2={x0} y2={y1}
                            stroke={CLOSED_CLR} strokeWidth={CLOSED_SW} strokeLinecap="square" />
                        )}
                        {/* Selection dashed border */}
                        {isSelected && (
                          <rect x={x0} y={y0}
                            width={x1 - x0} height={y1 - y0}
                            fill="none" stroke={border}
                            strokeWidth={2.5 / zoom}
                            strokeDasharray={`${5/zoom} ${2.5/zoom}`}
                            rx={1/zoom} />
                        )}
                        {/* Birleşik ortalanmış etiket bloğu: stand no → şirket adı → bayrak → m² */}
                        {placedLabels.map((it, i) => it.kind === 'flag' ? (
                          <text key={`lbl-${i}`}
                            x={cx} y={it.centerY}
                            textAnchor="middle" dominantBaseline="middle"
                            fontSize={it.font}
                            style={{ userSelect: 'none' }}>
                            {it.text}
                          </text>
                        ) : (
                          <text key={`lbl-${i}`}
                            x={cx} y={it.centerY}
                            textAnchor="middle" dominantBaseline="middle"
                            fontSize={it.font}
                            fontWeight={it.kind === 'name' ? 500 : 400}
                            fill={FONT_CLR}
                            fontFamily={it.kind === 'no' ? 'monospace' : 'sans-serif'}
                            opacity={it.kind === 'area' ? 0.9 : (it.kind === 'no' ? 0.95 : 1)}
                            stroke={HALO_CLR} strokeWidth={it.font * 0.12}
                            paintOrder="stroke">
                            {it.text}
                          </text>
                        ))}
                      </g>
                    );
                  })}

                  {/* 7 ─ Drag preview */}
                  {renderDragPreview()}

                  {/* 8 ─ Resize handles (when stand selected, not interacting) */}
                  {!interactMode && renderResizeHandles()}

                  {/* 9 ─ Move/Resize live preview */}
                  {renderPreviewLayer()}

                  {/* 10 ─ Annotations (measure / shapes / text / logo) */}
                  {renderAnnotations()}
                  {renderAnnDraft()}

                </g>
              </svg>
              {/* Floating HTML toolbar — positioned above selected stand */}
              {selectedStand && activeTool === 'stand' && !interactMode && renderFloatingToolbar()}
            </div>

            {/* Legend — strict 3-status palette: green=Available, orange=Reserved/Assigned, red=Sold */}
            <div className="flex items-center flex-wrap gap-3 mt-2 px-1 text-xs text-gray-500">
              {Object.entries(STATUS_BTN).map(([k, v]) => (
                <span key={k} className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-sm inline-block border"
                    style={{ background: hexToRgba(v.bg, gridSettings.transparency/100), borderColor: v.bg }} />
                  {v.label}
                </span>
              ))}
              {Object.entries(INFRA_TYPES).map(([k, v]) => (
                <span key={k} className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-sm inline-block border"
                    style={{ background: v.fill, borderColor: v.border }} />
                  {language === 'en' ? v.labelEn : v.label}
                </span>
              ))}
            </div>
          </div>

          {/* ── Right panel ─────────────────────────────────────────────────── */}
          <div className="w-72 flex-shrink-0 flex flex-col gap-3"
            style={{ maxHeight: 'calc(100vh - 185px)', overflowY: 'auto' }}>

            {selectedStand ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: getStandBorder(selectedStand) }} />
                    <div>
                      <span className="font-bold text-gray-800 block">{selectedStand.stand_no}</span>
                      {selectedStand.stand_name && <span className="text-xs text-gray-500">{selectedStand.stand_name}</span>}
                    </div>
                  </div>
                  <button onClick={() => setSelectedStand(null)} className="text-gray-300 hover:text-gray-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-gray-50 rounded-xl p-2 text-center">
                    <p className="font-bold text-gray-800">{cellsToArea((selectedStand.cells || []).length)}</p>
                    <p className="text-xs text-gray-400">m²</p>
                  </div>
                  <div className="rounded-xl p-2 text-center"
                    style={{ background: hexToRgba(getStandBorder(selectedStand), 0.12) }}>
                    <p className="text-xs font-bold" style={{ color: getStandBorder(selectedStand) }}>
                      {selectedStand.tenant_id
                        ? STATUS_BTN[effectiveStatus(selectedStand)]?.label || ''
                        : STATUS_BTN[selectedStand.status||'available']?.label}
                    </p>
                  </div>
                </div>

                {/* Price info */}
                {selectedStand.price_per_sqm && (
                  <div className="bg-green-50 rounded-xl px-3 py-2 mb-3 text-xs text-green-700">
                    <strong>{selectedStand.price_per_sqm} {selectedStand.currency || 'USD'}/m²</strong>
                    {' '}· Toplam: <strong>{(selectedStand.price_per_sqm * cellsToArea((selectedStand.cells||[]).length)).toFixed(0)} {selectedStand.currency || 'USD'}</strong>
                  </div>
                )}

                {/* Status buttons — always shown, Müsait triggers unassign confirm when tenant exists */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-500 mb-1.5">Durum</p>
                  <div className="grid grid-cols-3 gap-1">
                    {Object.entries(STATUS_BTN).map(([k, v]) => (
                      <button key={k} onClick={() => updateStatus(k)}
                        className="py-1.5 rounded-lg text-[11px] font-semibold border-2 transition-all"
                        style={{
                          background: effectiveStatus(selectedStand) === k ? hexToRgba(v.bg,0.2) : 'white',
                          borderColor: effectiveStatus(selectedStand) === k ? v.bg : '#e5e7eb',
                          color: v.bg
                        }}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedStand.tenant_name && (
                  <div className="bg-blue-50 rounded-xl px-3 py-2 mb-3 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {selectedStand.tenant_name[0].toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-blue-800 truncate">{selectedStand.tenant_name}</span>
                  </div>
                )}

                {/* Phase 86AU — Stand-type badge. Surfaces whether the
                    assigned stand is "Modular" or "Space Only" so admins,
                    customers, and reports all agree on the type at a glance. */}
                {selectedStand.tenant_id && selectedStand.stand_type && (
                  <div className="mb-3" data-testid="stand-type-badge">
                    {selectedStand.stand_type === 'modular' ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                        <LayoutGrid className="w-3 h-3" />
                        {tr ? 'Modüler Stand' : 'Modular Stand'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-sky-100 text-sky-700 border border-sky-200">
                        <Square className="w-3 h-3" />
                        {tr ? 'Sadece Alan' : 'Space Only'}
                      </span>
                    )}
                  </div>
                )}

                {/* Ownership badge — surfaces who created / assigned this stand
                    so admins know whose territory they're touching. Critical
                    UX for the new "only the assigner can modify" rule. */}
                {(selectedStand.created_by_admin_name || selectedStand.assigned_by_admin_name) && (
                  <div className="rounded-xl px-3 py-2 mb-3 text-[11px] leading-snug border border-amber-200 bg-amber-50 text-amber-800" data-testid="stand-ownership-badge">
                    {selectedStand.assigned_by_admin_name && selectedStand.tenant_id && (
                      <div>
                        <span className="opacity-60">{tr ? 'Atayan' : 'Assigned by'}: </span>
                        <strong>{selectedStand.assigned_by_admin_name}</strong>
                      </div>
                    )}
                    {selectedStand.created_by_admin_name && (
                      <div>
                        <span className="opacity-60">{tr ? 'Oluşturan' : 'Created by'}: </span>
                        <strong>{selectedStand.created_by_admin_name}</strong>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="space-y-2">
                  {(() => {
                    const updOwn = ownershipFor(selectedStand, 'update');
                    const blockedUpd = !updOwn.allowed;
                    return (
                      <button onClick={() => guardedRun(selectedStand, 'update', () => openEditModal(selectedStand), 'düzenle', 'edit')}
                        disabled={blockedUpd}
                        title={blockedUpd ? `${tr ? 'Sadece' : 'Only'} ${updOwn.ownerName}` : ''}
                        data-testid="stand-edit-btn"
                        className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl border text-sm transition-colors ${blockedUpd ? 'border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50' : 'border-purple-200 text-purple-600 hover:bg-purple-50'}`}>
                        <Pencil className="w-4 h-4" /> Düzenle
                      </button>
                    );
                  })()}
                  <button onClick={() => copyStand(selectedStand)}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-gray-200 text-gray-600 text-sm hover:bg-gray-50 transition-colors">
                    <Copy className="w-4 h-4" /> Kopyala
                  </button>
                  {selectedStand.tenant_id ? (() => {
                    const own = ownershipFor(selectedStand, 'unassign');
                    const blocked = !own.allowed;
                    return (
                      <>
                        <button onClick={() => guardedRun(selectedStand, 'unassign', () => unassignTenant(), 'atamayı kaldır', 'unassign')}
                          disabled={blocked}
                          title={blocked ? `${tr ? 'Sadece' : 'Only'} ${own.ownerName}` : ''}
                          data-testid="stand-unassign-btn"
                          className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl border text-sm ${blocked ? 'border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50' : 'border-red-200 text-red-500 hover:bg-red-50'}`}>
                          <UserMinus className="w-4 h-4" /> {tr ? 'Atamayı Kaldır' : 'Unassign'}
                        </button>
                        {/* "Move to Another Hall" — opens cross-hall picker.
                            Re-uses backend assign endpoint which already
                            handles freeing the source stand atomically. */}
                        <button onClick={() => guardedRun(selectedStand, 'unassign', () => openMoveAcrossHalls(), tr ? 'taşı' : 'move', 'move')}
                          disabled={blocked}
                          title={blocked ? `${tr ? 'Sadece' : 'Only'} ${own.ownerName}` : ''}
                          data-testid="stand-move-cross-hall-btn"
                          className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl border text-sm ${blocked ? 'border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50' : 'border-indigo-200 text-indigo-600 hover:bg-indigo-50'}`}>
                          <MoveRight className="w-4 h-4" />
                          {tr ? 'Başka Hall\'a Taşı' : 'Move to Another Hall'}
                        </button>
                      </>
                    );
                  })() : (
                    <button data-testid="assign-tenant-btn"
                      onClick={() => { setAssignStandType('space_only'); setAssignModularPkgId(''); fetchTenants(); setShowAssignModal(true); }}
                      className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-white text-sm font-semibold"
                      style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>
                      <UserPlus className="w-4 h-4" /> Katılımcı Ata
                    </button>
                  )}
                  {(() => {
                    const delOwn = ownershipFor(selectedStand, 'delete');
                    const blockedDel = !delOwn.allowed;
                    return (
                      <button onClick={() => guardedRun(selectedStand, 'delete', () => deleteStand(selectedStand.id), 'sil', 'delete')}
                        disabled={blockedDel}
                        title={blockedDel ? `${tr ? 'Sadece' : 'Only'} ${delOwn.ownerName}` : ''}
                        data-testid="stand-delete-btn"
                        className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl border text-sm ${blockedDel ? 'border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                        <Trash2 className="w-4 h-4" /> Sil
                      </button>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-center text-sm text-gray-400">
                {activeTool === 'stand'
                  ? t('Drag to draw a new stand or click an existing stand','Sürükleyerek yeni stand çizin veya mevcut standa tıklayın')
                  : `${tl(TOOL_BUTTONS.find(b=>b.id===activeTool)||{label:activeTool,labelEn:activeTool})} ${t('mode — click cells','modu — hücrelere tıklayın')}`}
              </div>
            )}

            {/* Stand list */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <h3 className="font-semibold text-gray-700 text-sm mb-3">{t('Stand List','Stand Listesi')} ({stands.length})</h3>
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {stands.length === 0 && <p className="text-xs text-gray-400 text-center py-4">{t('No stands yet','Henüz stand yok')}</p>}
                {stands.map(s => {
                  // Phase 86AZ.3 — surface stand_type + assigned package
                  // name directly in the list so admins don't need to open
                  // the detail panel to see what's bound to each stand.
                  const sType = s.stand_type; // 'modular' | 'space_only' | undefined
                  const pkg = sType === 'modular' && s.assigned_modular_package_id
                    ? (modularPackages || []).find(p => p.id === s.assigned_modular_package_id)
                    : null;
                  const pkgLabel = pkg
                    ? ((tr ? (pkg.name_tr || pkg.name) : (pkg.name_en || pkg.name)) || pkg.id)
                    : null;
                  return (
                  <button key={s.id}
                    onClick={() => { setSelectedStand(s); setActiveTool('stand'); }}
                    className={`w-full flex items-start gap-2 px-3 py-2 rounded-xl transition-all text-left ${
                      selectedStand?.id === s.id ? 'bg-purple-50 border border-purple-200' : 'hover:bg-gray-50'
                    }`}>
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: getStandBorder(s) }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-semibold text-gray-700 break-words">{s.stand_no}</p>
                        {sType === 'modular' && (
                          <span className="inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 leading-none whitespace-nowrap">
                            {tr ? 'Modüler' : 'Modular'}
                          </span>
                        )}
                        {sType === 'space_only' && (
                          <span className="inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 leading-none whitespace-nowrap">
                            {tr ? 'Sadece Alan' : 'Space Only'}
                          </span>
                        )}
                      </div>
                      {s.stand_name && <p className="text-xs text-gray-400 break-words mt-0.5">{s.stand_name}</p>}
                      {s.tenant_name && <p className="text-xs text-blue-500 break-words mt-0.5">{s.tenant_name}</p>}
                      {pkgLabel && (
                        <p className="text-[10px] text-emerald-600 break-words mt-0.5 flex items-center gap-1" title={pkgLabel}>
                          <span className="inline-block w-1 h-1 rounded-full bg-emerald-500 flex-shrink-0" />
                          <span className="truncate">{pkgLabel}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-xs text-gray-400">{cellsToArea((s.cells||[]).length)}m²</span>
                      {s.tenant_id
                        ? <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                        : <AlertCircle className="w-3.5 h-3.5" style={{ color: getStandBorder(s) }} />}
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          STAND MODAL — Create or Edit
          ════════════════════════════════════════════════════════════════ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-auto"
          onClick={() => setShowModalTenantDrop(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  {editingStand ? 'Stand Düzenle' : 'Yeni Stand Oluştur'}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {editingStand
                    ? `${cellsToArea((editingStand.cells||[]).length)}m² alan`
                    : (() => {
                        if (!pendingCells.length) return `${cellsToArea(pendingCells.length)}m² alan`;
                        const cols = pendingCells.map(c => c.col);
                        const rows = pendingCells.map(c => c.row);
                        const w = Math.max(...cols) - Math.min(...cols) + 1;
                        const h = Math.max(...rows) - Math.min(...rows) + 1;
                        return `${cellsToArea(pendingCells.length)}m² alan (${cellsToM(w)}m × ${cellsToM(h)}m)`;
                      })()}
                </p>
              </div>
              <button onClick={() => { setShowModal(false); setPendingCells([]); setEditingStand(null); setModalTenantId(null); setModalTenantSearch(''); setShowModalTenantDrop(false); }}
                className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">

              {/* Müşteri Seçimi */}
              <div className="relative" ref={tenantPickerRef}>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Müşteri / Katılımcı</label>
                <div className="relative">
                  <input
                    value={modalTenantSearch}
                    onChange={e => { setModalTenantSearch(e.target.value); setShowModalTenantDrop(true); }}
                    onFocus={() => setShowModalTenantDrop(true)}
                    placeholder="Müşteri ara veya seç..."
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400 pr-8"
                  />
                  {modalTenantId ? (
                    <button
                      type="button"
                      onClick={() => { setModalTenantId(null); setModalTenantSearch(''); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-400">
                      <X size={14} />
                    </button>
                  ) : (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                    </span>
                  )}
                </div>
                {showModalTenantDrop && (
                  <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-xl mt-1 overflow-hidden">
                    <div className="max-h-44 overflow-y-auto">
                      {tenants
                        .filter(t => !t.assigned_stand || t.id === modalTenantId)
                        .filter(t => t.name.toLowerCase().includes(modalTenantSearch.toLowerCase()))
                        .map(t => (
                          <button key={t.id} type="button"
                            onClick={() => {
                              setModalTenantId(t.id);
                              setModalTenantSearch(t.name);
                              // Auto-fill Stand Adı with customer name (editable) + set status to Rezerve
                              setModalData(prev => ({ ...prev, stand_name: t.name, status: 'reserved' }));
                              setShowModalTenantDrop(false);
                            }}
                            className="w-full px-3 py-2.5 text-left text-sm hover:bg-purple-50 flex items-center justify-between gap-2 transition-colors">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                                style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                                {t.name[0]?.toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium text-gray-700">{t.name}</p>
                                <p className="text-[10px] text-gray-400">{t.slug}</p>
                              </div>
                            </div>
                            {t.id === modalTenantId && <CheckCircle size={14} className="text-purple-500 flex-shrink-0" />}
                          </button>
                        ))}
                      {tenants.filter(t => !t.assigned_stand || t.id === modalTenantId).filter(t => t.name.toLowerCase().includes(modalTenantSearch.toLowerCase())).length === 0 && (
                        <p className="text-sm text-gray-400 px-3 py-3 text-center">Müşteri bulunamadı</p>
                      )}
                    </div>
                    <div className="border-t border-gray-100">
                      <button type="button"
                        onClick={() => { setModalTenantId(null); setModalTenantSearch(''); setShowModalTenantDrop(false); }}
                        className="w-full px-3 py-2 text-left text-xs text-gray-400 hover:bg-gray-50 transition-colors">
                        — Müşterisiz bırak
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Stand No + Adı */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Stand No</label>
                  <input data-testid="stand-no-input"
                    value={modalData.stand_no}
                    onChange={e => setModalData(p => ({ ...p, stand_no: e.target.value }))}
                    placeholder={t('Optional','İsteğe bağlı')}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Stand Adı</label>
                  <input
                    value={modalData.stand_name}
                    onChange={e => setModalData(p => ({ ...p, stand_name: e.target.value }))}
                    placeholder="Ana Sergi Alanı"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400" />
                </div>
              </div>

              {/* Boyut (Genişlik / Derinlik) */}
              <div className="bg-purple-50 rounded-xl p-3">
                <label className="text-xs font-semibold text-purple-700 block mb-2">Boyut (metre) — elle değiştirilebilir</label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-gray-500 mb-0.5 block">Genişlik (m)</label>
                    <input
                      type="number" min={gridUnit} step={gridUnit} max={cellsToM(hall?.width || 200)}
                      data-testid="stand-width-input"
                      value={(() => {
                        const cells = editingStand ? (editingStand.cells || []) : pendingCells;
                        if (!cells.length) return cellsToM(1);
                        const cols = cells.map(c => c.col);
                        return cellsToM(Math.max(...cols) - Math.min(...cols) + 1);
                      })()}
                      onChange={e => {
                        const cells = editingStand ? (editingStand.cells || []) : pendingCells;
                        if (!cells.length) return;
                        const rows = cells.map(c => c.row);
                        handleModalSizeChange(mToCells(e.target.value), Math.max(...rows) - Math.min(...rows) + 1);
                      }}
                      className="w-full border border-purple-200 bg-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 mb-0.5 block">Derinlik (m)</label>
                    <input
                      type="number" min={gridUnit} step={gridUnit} max={cellsToM(hall?.height || 200)}
                      data-testid="stand-height-input"
                      value={(() => {
                        const cells = editingStand ? (editingStand.cells || []) : pendingCells;
                        if (!cells.length) return cellsToM(1);
                        const rows = cells.map(c => c.row);
                        return cellsToM(Math.max(...rows) - Math.min(...rows) + 1);
                      })()}
                      onChange={e => {
                        const cells = editingStand ? (editingStand.cells || []) : pendingCells;
                        if (!cells.length) return;
                        const cols = cells.map(c => c.col);
                        handleModalSizeChange(Math.max(...cols) - Math.min(...cols) + 1, mToCells(e.target.value));
                      }}
                      className="w-full border border-purple-200 bg-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400" />
                  </div>
                </div>
                <p className="text-[10px] text-purple-500 mt-1.5">
                  Toplam: <strong>{cellsToArea((editingStand ? (editingStand.cells||[]) : pendingCells).length)}m²</strong>
                </p>
              </div>

              {/* Stand Durumu */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Stand Durumu</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(STATUS_BTN).map(([k, v]) => (
                    <button key={k}
                      onClick={() => {
                        // If switching to Available while tenant is assigned → confirm unassign
                        if (k === 'available' && modalTenantId) {
                          setUnassignSource('modal');
                          setShowUnassignConfirm(true);
                        } else {
                          setModalData(p => ({ ...p, status: k }));
                        }
                      }}
                      className="py-2 rounded-xl text-xs font-semibold border-2 transition-all"
                      style={{
                        background: modalData.status === k ? hexToRgba(v.bg, 0.15) : 'white',
                        borderColor: modalData.status === k ? v.bg : '#e5e7eb',
                        color: v.bg
                      }}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Phase 86AU — Stand Type (Modular / Space Only) — Green Zone halls only.
                  Modular is hard-blocked when the stand's m² isn't in the
                  configured Modular Stand Area Options; the rule is shown
                  inline so admins know why and where to change it. */}
              {hall?.zone === 'green_zone' && (() => {
                const standAreaM2 = cellsToArea((editingStand?.cells?.length) || (pendingCells?.length) || 0);
                const modularAllowed = modularAreas.includes(standAreaM2);
                return (
                  <div className="border border-emerald-100 bg-emerald-50/40 rounded-xl p-3" data-testid="edit-stand-type-block">
                    <label className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 block mb-2">
                      {tr ? 'Stand Türü' : 'Stand Type'}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        data-testid="edit-stand-type-none"
                        onClick={() => setModalData(p => ({ ...p, stand_type: '' }))}
                        className={`px-2 py-2 rounded-lg text-[11px] font-semibold transition-colors border ${
                          !modalData.stand_type
                            ? 'bg-gray-700 text-white border-gray-700'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}>
                        {tr ? 'Belirsiz' : 'Untagged'}
                      </button>
                      <button
                        type="button"
                        data-testid="edit-stand-type-space"
                        onClick={() => setModalData(p => ({ ...p, stand_type: 'space_only' }))}
                        className={`px-2 py-2 rounded-lg text-[11px] font-semibold transition-colors border ${
                          modalData.stand_type === 'space_only'
                            ? 'bg-sky-600 text-white border-sky-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}>
                        {tr ? 'Sadece Alan' : 'Space Only'}
                      </button>
                      <button
                        type="button"
                        data-testid="edit-stand-type-modular"
                        onClick={() => modularAllowed && setModalData(p => ({ ...p, stand_type: 'modular' }))}
                        disabled={!modularAllowed}
                        title={!modularAllowed
                          ? (tr
                              ? `Bu stand ${standAreaM2} m². İzinli modüler alanlar: ${modularAreas.join(', ')} m².`
                              : `This stand is ${standAreaM2} m². Allowed modular areas: ${modularAreas.join(', ')} m².`)
                          : ''}
                        className={`px-2 py-2 rounded-lg text-[11px] font-semibold transition-colors border ${
                          !modularAllowed
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                            : modalData.stand_type === 'modular'
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}>
                        {tr ? 'Modüler' : 'Modular'}
                        {!modularAllowed && <span className="ml-1 text-[10px]">✕</span>}
                      </button>
                    </div>
                    {!modularAllowed && (
                      <p className="text-[11px] text-amber-700 mt-2 leading-relaxed">
                        {tr
                          ? `Modüler kapalı — ${standAreaM2} m² izinli listede yok (${modularAreas.join(', ')} m²). Ayarlar → Modüler Stand Alan Seçenekleri'nde düzenleyin.`
                          : `Modular disabled — ${standAreaM2} m² is not in the allowed list (${modularAreas.join(', ')} m²). Edit it in Settings → Modular Stand Area Options.`}
                      </p>
                    )}

                    {/* Phase 86AZ.2 — Modular Package binding (visible only
                        when stand_type='modular'). Lists ALL active packages
                        but disables the ones whose area_m2 ≠ stand area so
                        the admin sees exactly why a package isn't pickable.
                        Backend `_validate_modular_package` enforces the same
                        rule (HTTP 400 on mismatch). */}
                    {modalData.stand_type === 'modular' && modularAllowed && (() => {
                      const allActive = (modularPackages || []).filter(p => p.is_active);
                      const matching  = allActive.filter(p => Number(p.area_m2) === Number(standAreaM2));
                      return (
                        <div className="mt-3" data-testid="edit-modular-package-block">
                          <label className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 mb-1 block">
                            {tr ? 'Modüler Paket' : 'Modular Package'}
                          </label>
                          {allActive.length === 0 ? (
                            <p className="text-[11px] text-amber-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                              {tr
                                ? 'Aktif modüler paket bulunamadı. Önce Modüler Standlar → Paketler ekranından paket oluşturun.'
                                : 'No active modular packages. Create one first under Modular Stands → Packages.'}
                            </p>
                          ) : (
                            <>
                              <select
                                value={modalData.assigned_modular_package_id || ''}
                                onChange={e => setModalData(p => ({ ...p, assigned_modular_package_id: e.target.value }))}
                                data-testid="edit-modular-package-select"
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-emerald-400 bg-white"
                              >
                                <option value="">{tr ? '— Paket seçin —' : '— Select a package —'}</option>
                                {allActive.map(p => {
                                  const mismatch = Number(p.area_m2) !== Number(standAreaM2);
                                  const label = `${(tr ? (p.name_tr || p.name) : (p.name_en || p.name)) || p.id} · ${p.area_m2} m² · ${p.currency || 'EUR'} ${Number(p.total_price || 0).toLocaleString()}`;
                                  return (
                                    <option key={p.id} value={p.id} disabled={mismatch} title={mismatch ? (tr ? `Bu paket ${p.area_m2} m² — stand ${standAreaM2} m². Eşleşmiyor.` : `Package is ${p.area_m2} m² — stand is ${standAreaM2} m². Mismatch.`) : ''}>
                                      {mismatch ? '⛔ ' : ''}{label}{mismatch ? ` — ${tr ? 'alan eşleşmiyor' : 'area mismatch'}` : ''}
                                    </option>
                                  );
                                })}
                              </select>
                              <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">
                                {matching.length === 0
                                  ? (tr
                                      ? `${standAreaM2} m² için tanımlı paket yok. Sadece stand alanına eşit m² olan paketler seçilebilir.`
                                      : `No package defined for ${standAreaM2} m². Only packages whose m² equals the stand area are selectable.`)
                                  : (tr
                                      ? `${matching.length}/${allActive.length} paket bu standa uygun (${standAreaM2} m²). Diğerleri farklı m²'de.`
                                      : `${matching.length}/${allActive.length} packages match this stand (${standAreaM2} m²). Others are different m².`)}
                              </p>
                              <p className="text-[10px] text-gray-500 leading-relaxed">
                                {tr
                                  ? 'Müşteri yalnızca seçilen paketi görür. Özel tasarım yolu kapalıdır.'
                                  : 'Customer will only see this package. Custom design path is locked.'}
                              </p>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* m² Birim Fiyatı */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">m² Birim Fiyatı</label>
                <div className="flex gap-2">
                  <input
                    type="number" min="0"
                    value={modalData.price_per_sqm}
                    onChange={e => setModalData(p => ({ ...p, price_per_sqm: e.target.value }))}
                    placeholder="0.00"
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400" />
                  <select
                    value={modalData.currency}
                    onChange={e => setModalData(p => ({ ...p, currency: e.target.value }))}
                    className="border border-gray-200 rounded-xl px-2 py-2 text-sm focus:outline-none focus:border-purple-400">
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                  {modalData.price_per_sqm && (
                    <div className="flex items-center bg-green-50 rounded-xl px-3 text-xs font-semibold text-green-700 whitespace-nowrap">
                      {(parseFloat(modalData.price_per_sqm) * cellsToArea(editingStand ? (editingStand.cells||[]).length : pendingCells.length)).toFixed(0)} {modalData.currency}
                    </div>
                  )}
                </div>
              </div>

              {/* Renk + Şeffaflık */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Renk</label>
                  <div className="flex items-center gap-2">
                    <input type="color"
                      value={modalData.color}
                      onChange={e => setModalData(p => ({ ...p, color: e.target.value }))}
                      className="w-10 h-9 border border-gray-200 rounded-lg cursor-pointer p-0.5" />
                    <input
                      value={modalData.color}
                      onChange={e => setModalData(p => ({ ...p, color: e.target.value }))}
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:border-purple-400" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">
                    Şeffaflık — {modalData.transparency}%
                  </label>
                  <div className="flex items-center gap-2 mt-2">
                    <input type="range" min="10" max="100" step="5"
                      value={modalData.transparency}
                      onChange={e => setModalData(p => ({ ...p, transparency: parseInt(e.target.value) }))}
                      className="flex-1" />
                    <div className="w-8 h-8 rounded-lg border border-gray-200"
                      style={{ background: hexToRgba(modalData.color, modalData.transparency/100) }} />
                  </div>
                </div>
              </div>

              {/* m² Göster */}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="show_area" checked={modalData.show_area}
                  onChange={e => setModalData(p => ({ ...p, show_area: e.target.checked }))}
                  className="w-4 h-4 accent-purple-600" />
                <label htmlFor="show_area" className="text-sm text-gray-600">{t('Show m² area on stand','m² alanı stand üzerinde göster')}</label>
              </div>

              {/* Açık Kenarlar */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{t('Open Edges (Corridor Sides)','Açık Kenarlar (Koridor Yönleri)')}</p>
                <p className="text-xs text-gray-400 mb-3">{t("Which edges of the stand face a corridor? (Selected = open edge)",'Standın hangi kenarları koridora açık? (Seçili = açık kenar)')}</p>
                {/* Compass-style edge selector */}
                <div className="flex flex-col items-center gap-1">
                  {/* North */}
                  <button type="button"
                    onClick={() => {
                      const sides = modalData.open_sides ?? ['north','south','east','west'];
                      const next = sides.includes('north') ? sides.filter(s => s !== 'north') : [...sides, 'north'];
                      setModalData(p => ({ ...p, open_sides: next }));
                    }}
                    className="px-6 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all"
                    style={{
                      background: (modalData.open_sides ?? ['north','south','east','west']).includes('north') ? 'rgba(34,197,94,0.12)' : '#f1f5f9',
                      borderColor: (modalData.open_sides ?? ['north','south','east','west']).includes('north') ? '#22c55e' : '#e2e8f0',
                      color: (modalData.open_sides ?? ['north','south','east','west']).includes('north') ? '#16a34a' : '#94a3b8'
                    }}>
                    ↑ Kuzey (Üst)
                  </button>
                  <div className="flex items-center gap-1">
                    {/* West */}
                    <button type="button"
                      onClick={() => {
                        const sides = modalData.open_sides ?? ['north','south','east','west'];
                        const next = sides.includes('west') ? sides.filter(s => s !== 'west') : [...sides, 'west'];
                        setModalData(p => ({ ...p, open_sides: next }));
                      }}
                      className="px-4 py-3 rounded-lg text-xs font-semibold border-2 transition-all"
                      style={{
                        background: (modalData.open_sides ?? ['north','south','east','west']).includes('west') ? 'rgba(34,197,94,0.12)' : '#f1f5f9',
                        borderColor: (modalData.open_sides ?? ['north','south','east','west']).includes('west') ? '#22c55e' : '#e2e8f0',
                        color: (modalData.open_sides ?? ['north','south','east','west']).includes('west') ? '#16a34a' : '#94a3b8'
                      }}>
                      ← Batı
                    </button>
                    {/* Center stand representation */}
                    <div className="w-16 h-10 rounded-lg flex items-center justify-center text-xs text-gray-400 font-medium"
                      style={{ background: hexToRgba(modalData.color, 0.3), border: `2px solid ${modalData.color}` }}>
                      STAND
                    </div>
                    {/* East */}
                    <button type="button"
                      onClick={() => {
                        const sides = modalData.open_sides ?? ['north','south','east','west'];
                        const next = sides.includes('east') ? sides.filter(s => s !== 'east') : [...sides, 'east'];
                        setModalData(p => ({ ...p, open_sides: next }));
                      }}
                      className="px-4 py-3 rounded-lg text-xs font-semibold border-2 transition-all"
                      style={{
                        background: (modalData.open_sides ?? ['north','south','east','west']).includes('east') ? 'rgba(34,197,94,0.12)' : '#f1f5f9',
                        borderColor: (modalData.open_sides ?? ['north','south','east','west']).includes('east') ? '#22c55e' : '#e2e8f0',
                        color: (modalData.open_sides ?? ['north','south','east','west']).includes('east') ? '#16a34a' : '#94a3b8'
                      }}>
                      Doğu →
                    </button>
                  </div>
                  {/* South */}
                  <button type="button"
                    onClick={() => {
                      const sides = modalData.open_sides ?? ['north','south','east','west'];
                      const next = sides.includes('south') ? sides.filter(s => s !== 'south') : [...sides, 'south'];
                      setModalData(p => ({ ...p, open_sides: next }));
                    }}
                    className="px-6 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all"
                    style={{
                      background: (modalData.open_sides ?? ['north','south','east','west']).includes('south') ? 'rgba(34,197,94,0.12)' : '#f1f5f9',
                      borderColor: (modalData.open_sides ?? ['north','south','east','west']).includes('south') ? '#22c55e' : '#e2e8f0',
                      color: (modalData.open_sides ?? ['north','south','east','west']).includes('south') ? '#16a34a' : '#94a3b8'
                    }}>
                    ↓ Güney (Alt)
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-2 text-center">
                  {(() => {
                    const s = modalData.open_sides ?? ['north','south','east','west'];
                    if (s.length === 0) return 'Tüm kenarlar kapalı (duvar)';
                    if (s.length === 4) return 'Ada stand — 4 kenar açık';
                    return `${s.length} açık kenar: ${s.map(x => ({north:'Kuzey',south:'Güney',east:'Doğu',west:'Batı'})[x]).join(', ')}`;
                  })()}
                </p>
              </div>

              {/* Yükseklik bilgileri */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Yükseklik Bilgileri</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">Tavan Yüksekliği (m)</label>
                    <input type="number" min="0" step="0.1"
                      value={modalData.ceiling_height}
                      onChange={e => setModalData(p => ({ ...p, ceiling_height: e.target.value }))}
                      placeholder={hall?.ceiling_height != null ? String(hall.ceiling_height) : '6.0'}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">Maks. Stand Yüksekliği (m)</label>
                    <input type="number" min="0" step="0.1"
                      value={modalData.max_stand_height}
                      onChange={e => setModalData(p => ({ ...p, max_stand_height: e.target.value }))}
                      placeholder={hall?.max_stand_height != null ? String(hall.max_stand_height) : '5.0'}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400" />
                  </div>
                </div>
              </div>

              {/* Müşteri görünümü */}
              <div className="border-t border-gray-100 pt-3">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Müşteri Görünümü</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={modalData.hide_status}
                      onChange={e => setModalData(p => ({ ...p, hide_status: e.target.checked }))}
                      className="w-4 h-4 accent-purple-600" />
                    <span className="text-sm text-gray-600">Müşteri satış statüsünü görmesin</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={modalData.hide_company}
                      onChange={e => setModalData(p => ({ ...p, hide_company: e.target.checked }))}
                      className="w-4 h-4 accent-purple-600" />
                    <span className="text-sm text-gray-600">Müşteri firma ismini görmesin</span>
                  </label>
                </div>
              </div>

            </div>

            {/* Modal footer */}
            <div className="px-6 pb-5 pt-3 border-t border-gray-100 flex gap-3">
              <button onClick={() => { setShowModal(false); setPendingCells([]); setEditingStand(null); setModalTenantId(null); setModalTenantSearch(''); setShowModalTenantDrop(false); }}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                Vazgeç
              </button>
              <button onClick={saveStand} disabled={savingStand}
                data-testid="stand-no-save-btn"
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                {savingStand ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Email Stand Modal ─────────────────────────────────────────────── */}
      {showEmailDialog && selectedStand && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">Stand Bilgisi Gönder</h2>
              <button onClick={() => { setShowEmailDialog(false); setEmailMsg(''); }}
                className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            {selectedStand.tenant_id ? (
              <p className="text-sm text-gray-600 mb-3">
                Alıcı: <strong className="text-blue-600">{selectedStand.tenant_name}</strong>
              </p>
            ) : (
              <p className="text-sm text-yellow-600 bg-yellow-50 rounded-xl px-3 py-2 mb-3">
                Bu stand henüz bir katılımcıya atanmamış.
              </p>
            )}
            <div className="bg-gray-50 rounded-xl p-3 mb-3 text-xs text-gray-600 space-y-1">
              <p><strong>Stand No:</strong> {selectedStand.stand_no}</p>
              {selectedStand.stand_name && <p><strong>Stand Adı:</strong> {selectedStand.stand_name}</p>}
              <p><strong>Alan:</strong> {cellsToArea((selectedStand.cells||[]).length)}m²</p>
              {selectedStand.price_per_sqm && (
                <p><strong>Toplam Fiyat:</strong> {(selectedStand.price_per_sqm * cellsToArea((selectedStand.cells||[]).length)).toFixed(0)} {selectedStand.currency || 'USD'}</p>
              )}
            </div>
            <textarea value={emailMsg} onChange={e => setEmailMsg(e.target.value)}
              placeholder="Ek mesaj (opsiyonel)..." rows={3}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-purple-400 mb-4 resize-none" />
            <div className="flex gap-3">
              <button onClick={() => { setShowEmailDialog(false); setEmailMsg(''); }}
                className="flex-1 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                Vazgeç
              </button>
              <button
                onClick={async () => {
                  setSendingEmail(true);
                  await new Promise(r => setTimeout(r, 600));
                  setSendingEmail(false);
                  setShowEmailDialog(false);
                  setEmailMsg('');
                }}
                disabled={!selectedStand.tenant_id || sendingEmail}
                className="flex-1 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                {sendingEmail ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assign Modal ──────────────────────────────────────────────────── */}
      {showAssignModal && (() => {
        // Phase 86AU — Green Zone stand-type gate: Modular is only available
        // when the stand area matches a configured Modular Stand Area Option.
        const standArea = cellsToArea(selectedStand?.cells?.length || 0);
        const modularAllowed = modularAreas.includes(standArea);
        const isGreenZone = hall?.zone === 'green_zone';
        return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Katılımcı Ata</h2>
                <p className="text-xs text-gray-500 mt-0.5">Stand: <strong>{selectedStand?.stand_no}</strong> · {standArea} m²</p>
              </div>
              <button onClick={() => { setShowAssignModal(false); setTenantSearch(''); }}
                className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            {/* Phase 86AU — Stand Type selector (Green Zone only). Modular
                is hard-blocked if the area isn't in modular_areas; the rule
                surfaces inline so the admin understands why. */}
            {isGreenZone && (
              <div className="mb-3 border border-emerald-100 bg-emerald-50/40 rounded-xl p-3" data-testid="assign-stand-type-block">
                <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 mb-2">
                  {tr ? 'Stand Türü' : 'Stand Type'}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="assign-stand-type-space"
                    onClick={() => setAssignStandType('space_only')}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors border ${
                      assignStandType === 'space_only'
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}>
                    {tr ? 'Sadece Alan (Space Only)' : 'Space Only'}
                  </button>
                  <button
                    type="button"
                    data-testid="assign-stand-type-modular"
                    onClick={() => modularAllowed && setAssignStandType('modular')}
                    disabled={!modularAllowed}
                    title={!modularAllowed
                      ? (tr
                          ? `Bu stand ${standArea} m². İzinli modüler alanlar: ${modularAreas.join(', ')} m².`
                          : `This stand is ${standArea} m². Allowed modular areas: ${modularAreas.join(', ')} m².`)
                      : ''}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors border ${
                      !modularAllowed
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : assignStandType === 'modular'
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}>
                    {tr ? 'Modüler' : 'Modular'}
                    {!modularAllowed && <span className="ml-1 text-[10px]">✕</span>}
                  </button>
                </div>
                {!modularAllowed && (
                  <p className="text-[11px] text-amber-700 mt-2 leading-relaxed" data-testid="assign-modular-blocked">
                    {tr
                      ? `Modüler seçenek bu alan için kapalı — ${standArea} m² izin verilen listede yok (${modularAreas.join(', ')} m²). Ayarlar → Modüler Stand Alan Seçenekleri'nde düzenleyebilirsiniz.`
                      : `Modular is disabled — ${standArea} m² is not in the allowed list (${modularAreas.join(', ')} m²). Edit it in Settings → Modular Stand Area Options.`}
                  </p>
                )}

                {/* Phase 86AZ.2 — Modular Stand Package binding. Lists ALL
                    active packages but disables those whose area_m2 ≠ the
                    stand area so the admin sees exactly why a package isn't
                    pickable (backend enforces the same rule). */}
                {modularAllowed && assignStandType === 'modular' && (() => {
                  const allActive = (modularPackages || []).filter(p => p.is_active);
                  const matching  = allActive.filter(p => Number(p.area_m2) === Number(standArea));
                  return (
                    <div className="mt-3" data-testid="assign-modular-package-block">
                      <label className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 mb-1 block">
                        {tr ? 'Modüler Paket' : 'Modular Package'}
                      </label>
                      {allActive.length === 0 ? (
                        <p className="text-[11px] text-amber-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                          {tr
                            ? 'Aktif modüler paket bulunamadı. Modüler Standlar → Paketler ekranından önce paket oluşturun.'
                            : 'No active modular packages. Create one first under Modular Stands → Packages.'}
                        </p>
                      ) : (
                        <>
                          <select
                            value={assignModularPkgId}
                            onChange={e => setAssignModularPkgId(e.target.value)}
                            data-testid="assign-modular-package-select"
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-emerald-400 bg-white"
                          >
                            <option value="">{tr ? '— Paket seçin —' : '— Select a package —'}</option>
                            {allActive.map(p => {
                              const mismatch = Number(p.area_m2) !== Number(standArea);
                              const label = `${(tr ? (p.name_tr || p.name) : (p.name_en || p.name)) || p.id} · ${p.area_m2} m² · ${p.currency || 'EUR'} ${Number(p.total_price || 0).toLocaleString()}`;
                              return (
                                <option key={p.id} value={p.id} disabled={mismatch} title={mismatch ? (tr ? `Bu paket ${p.area_m2} m² — stand ${standArea} m². Eşleşmiyor.` : `Package is ${p.area_m2} m² — stand is ${standArea} m². Mismatch.`) : ''}>
                                  {mismatch ? '⛔ ' : ''}{label}{mismatch ? ` — ${tr ? 'alan eşleşmiyor' : 'area mismatch'}` : ''}
                                </option>
                              );
                            })}
                          </select>
                          <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">
                            {matching.length === 0
                              ? (tr
                                  ? `${standArea} m² için tanımlı paket yok. Sadece stand alanına eşit m² olan paketler seçilebilir.`
                                  : `No package defined for ${standArea} m². Only packages whose m² equals the stand area are selectable.`)
                              : (tr
                                  ? `${matching.length}/${allActive.length} paket bu standa uygun (${standArea} m²). Diğerleri farklı m²'de.`
                                  : `${matching.length}/${allActive.length} packages match this stand (${standArea} m²). Others are different m².`)}
                          </p>
                          <p className="text-[10px] text-gray-500 leading-relaxed">
                            {tr
                              ? 'Müşteri yalnızca seçilen paketi görür. Özel tasarım seçeneği kapanır.'
                              : 'Customer will only see this package. Custom design path will be locked.'}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            <input value={tenantSearch} onChange={e => setTenantSearch(e.target.value)}
              placeholder="Katılımcı ara..."
              className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-purple-400 mb-3" />
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {filteredTenants.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-6">
                  {tenants.length === 0 ? 'Katılımcı bulunamadı' : 'Atanabilir katılımcı yok'}
                </p>
              )}
              {filteredTenants.map(t => (
                <button key={t.id}
                  onClick={() => assignTenant(t.id)}
                  disabled={assigning}
                  data-testid={`assign-tenant-${t.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left
                    ${t.assigned_stand
                      ? 'bg-amber-50 border-amber-200 hover:bg-amber-100 cursor-pointer'
                      : 'hover:bg-purple-50 border-transparent hover:border-purple-200 cursor-pointer disabled:opacity-50'
                    }`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                    style={{ background: t.assigned_stand ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}>
                    {t.name[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-700 truncate">{t.name}</p>
                    <p className="text-xs text-gray-400 truncate">{t.slug}</p>
                    {t.assigned_stand && (
                      <p className="text-[10px] text-amber-700 bg-white border border-amber-300 rounded-md px-1.5 py-0.5 inline-block mt-0.5 font-bold">
                        ⇄ {language === 'tr' ? 'Mevcut:' : 'Currently:'} {t.assigned_stand}
                        {t.assigned_hall_name ? ` — ${t.assigned_hall_name}` : ''} ({language === 'tr' ? 'taşımak için tıkla' : 'click to move'})
                      </p>
                    )}
                  </div>
                  {t.assigned_stand
                    ? <span className="text-[10px] text-amber-600 flex-shrink-0 font-bold">{language === 'tr' ? 'TAŞI →' : 'MOVE →'}</span>
                    : assigning && <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                  }
                </button>
              ))}
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Super Admin Override Confirmation Modal ────────────────────────
          When the active user is a Super Admin AND the stand is owned by a
          different admin, we make the override an explicit, audit-logged
          decision rather than a silent action. */}
      {overrideConfirm && (
        <div className="fixed inset-0 bg-black/55 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="super-admin-override-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-lg">
                  {tr ? 'Başka admin\'in işlemini geçersiz kılıyorsunuz' : 'Overriding another admin\'s action'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {tr ? 'Super Admin yetkisi · Audit log\'a kaydedilecek' : 'Super Admin override · will be audit-logged'}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-4 leading-relaxed">
              {tr ? 'Bu stand' : 'This stand'} <strong className="text-rose-700">{overrideConfirm.ownerName}</strong>
              {' '}{tr ? 'tarafından yönetiliyor.' : 'manages this stand.'}{' '}
              <span className="font-medium">
                {tr ? 'Yine de devam etmek istiyor musunuz?' : 'Do you want to continue anyway?'}
              </span>
            </p>
            <div className="flex gap-3">
              <button
                data-testid="override-cancel-btn"
                onClick={() => setOverrideConfirm(null)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button
                data-testid="override-confirm-btn"
                onClick={() => overrideConfirm.onConfirm && overrideConfirm.onConfirm()}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors"
                style={{ background: 'linear-gradient(135deg,#e11d48,#be123c)' }}>
                {tr ? 'Devam Et' : 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Unassign Confirmation Modal ────────────────────────────────────── */}
      {showUnassignConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800">{t('Remove Company Assignment','Firma Atamasını Kaldır')}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{t('This action cannot be undone','Bu işlem geri alınamaz')}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              {unassignSource === 'panel'
                ? <><strong className="text-blue-700">{selectedStand?.tenant_name}</strong> {t('is currently assigned to this stand. Do you want to remove the company and set the stand to','bu standa atanmış durumda. Firmayı kaldırmak ve standu')} <strong className="text-green-600">{t('Available','Müsait')}</strong> {t('status?','statüsüne döndürmek ister misiniz?')}</>
                : <>{t('The selected company was to be assigned to this stand. Do you want to remove the assignment and set it to','Seçili firma bu standa atanacaktı. Müsait statüsüne geçmek için firma atamasını kaldırmak ister misiniz?')} {t('Available','Müsait')} {t('status?','')}</>
              }
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowUnassignConfirm(false); setUnassignSource(null); }}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                {t('Cancel','İptal')}
              </button>
              <button
                onClick={handleUnassignConfirm}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold transition-colors"
                style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)' }}>
                {t('Yes, Remove','Evet, Kaldır')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Move (Reassign) Confirmation Modal ─────────────────────────── */}
      {moveConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-lg">{t('Move Participant','Katılımcıyı Taşı')}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{t('Confirm reassignment','Yeniden atamayı onaylayın')}</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              <strong className="text-amber-700">{moveConfirm.tenantName}</strong>{' '}
              {t('is currently assigned to','şu anda')}{' '}
              <strong>{moveConfirm.fromStand}</strong>
              {moveConfirm.fromHall ? ` (${moveConfirm.fromHall})` : ''}{' '}
              {t('. Do you really want to move them to','standına atanmış. Onu')}{' '}
              <strong className="text-emerald-700">{moveConfirm.toStand}</strong>
              {moveConfirm.toHall ? ` (${moveConfirm.toHall})` : ''}
              {t('?',' standına taşımak istediğinize emin misiniz?')}
            </p>
            <p className="text-xs text-gray-500 mb-5 bg-amber-50 border border-amber-200 rounded-lg p-3">
              {t('The previous stand will become Available again.','Eski stand otomatik olarak "Müsait" durumuna dönecek.')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setMoveConfirm(null)}
                disabled={assigning}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                data-testid="move-confirm-cancel"
              >
                {t('Cancel','İptal')}
              </button>
              <button
                onClick={() => assignTenant(moveConfirm.tenantId, true)}
                disabled={assigning}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}
                data-testid="move-confirm-yes"
              >
                {assigning
                  ? <span className="inline-flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{t('Moving...','Taşınıyor...')}</span>
                  : t('Yes, Move','Evet, Taşı')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cross-Hall Move: target picker ─────────────────────────────────
          Shown when the operator clicks "Move to Another Hall" on an
          assigned stand. Lists all OTHER halls grouped, with their
          currently-available stands. Clicking a target opens a confirm
          modal so a misclick can't silently relocate the tenant. */}
      {crossMoveOpen && (() => {
        const q = crossMoveSearch.trim().toLowerCase();
        const filtered = (crossMoveHalls || [])
          .map(h => ({
            ...h,
            stands: q
              ? h.stands.filter(s => (s.stand_no || '').toLowerCase().includes(q) || (h.hall_name || '').toLowerCase().includes(q))
              : h.stands,
          }))
          .filter(h => h.stands.length > 0);
        const total = filtered.reduce((sum, h) => sum + h.stands.length, 0);
        return (
          <div className="fixed inset-0 bg-black/55 z-[75] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" data-testid="cross-hall-move-modal">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <MoveRight className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">
                      {tr ? 'Başka Hall\'a Taşı' : 'Move to Another Hall'}
                    </h3>
                    <p className="text-xs text-gray-500">
                      <strong>{selectedStand?.tenant_name}</strong>
                      {' · '}{tr ? 'Hedef stand seçin' : 'pick destination stand'}
                    </p>
                  </div>
                </div>
                <button onClick={() => { setCrossMoveOpen(false); setCrossMoveSearch(''); }}
                  className="h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>

              {/* Search */}
              <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    autoFocus
                    placeholder={tr ? 'Hall veya stand no ara…' : 'Search hall or stand no…'}
                    value={crossMoveSearch}
                    onChange={e => setCrossMoveSearch(e.target.value)}
                    data-testid="cross-hall-search-input"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-auto px-6 py-4">
                {crossMoveLoading ? (
                  <div className="text-center py-12 text-gray-400 text-sm">{tr ? 'Yükleniyor…' : 'Loading…'}</div>
                ) : total === 0 ? (
                  <div className="text-center py-12 text-gray-400 text-sm">
                    {tr ? 'Diğer hall\'larda müsait stand yok.' : 'No available stands in other halls.'}
                  </div>
                ) : (
                  <div className="space-y-5">
                    {filtered.map(h => (
                      <div key={h.hall_id}>
                        <div className="flex items-center gap-2 mb-2">
                          <Building2 className="h-4 w-4 text-gray-500" />
                          <h4 className="font-bold text-gray-700 text-sm">{h.hall_name}</h4>
                          <span className="text-xs text-gray-400">· {h.stands.length} {tr ? 'müsait' : 'available'}</span>
                          {h.zone && <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{h.zone}</span>}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {h.stands.map(s => (
                            <button
                              key={s.id}
                              data-testid={`cross-hall-stand-${s.stand_no}`}
                              onClick={() => setCrossMovePending({
                                dstHallId:   h.hall_id,
                                dstHallName: h.hall_name,
                                dstStandId:  s.id,
                                dstStandNo:  s.stand_no,
                                area:        s.area,
                              })}
                              className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 text-left transition-colors">
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{s.stand_no}</p>
                                <p className="text-[10px] text-gray-400">{s.area} m²{s.price_per_sqm ? ` · ${s.price_per_sqm}${s.currency || ''}` : ''}</p>
                              </div>
                              <MoveRight className="h-3.5 w-3.5 text-indigo-500" />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Cross-Hall Move: confirm modal ────────────────────────────────── */}
      {crossMovePending && (
        <div className="fixed inset-0 bg-black/55 z-[80] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="cross-hall-confirm-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <MoveRight className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-lg">
                  {tr ? 'Cross-Hall Taşımayı Onayla' : 'Confirm Cross-Hall Move'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {tr ? 'Eski stand otomatik müsait olur · Audit log\'a yazılır' : 'Source stand auto-released · audit-logged'}
                </p>
              </div>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm space-y-2 mb-5">
              <div className="flex items-center gap-2">
                <strong className="text-gray-900">{selectedStand?.tenant_name}</strong>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wide text-gray-400">{tr ? 'Şuandan' : 'From'}</p>
                  <p className="font-semibold text-rose-600">{selectedStand?.stand_no} · {hall?.name}</p>
                </div>
                <MoveRight className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wide text-gray-400">{tr ? 'Hedef' : 'To'}</p>
                  <p className="font-semibold text-emerald-600">{crossMovePending.dstStandNo} · {crossMovePending.dstHallName}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="cross-hall-cancel"
                onClick={() => setCrossMovePending(null)}
                disabled={assigning}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
              <button
                data-testid="cross-hall-confirm"
                onClick={doCrossHallMove}
                disabled={assigning}
                className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#6366f1,#4338ca)' }}>
                {assigning
                  ? <span className="inline-flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{tr ? 'Taşınıyor…' : 'Moving…'}</span>
                  : (tr ? 'Evet, Taşı' : 'Yes, Move')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Column Add Modal ────────────────────────────────────────────── */}
      {columnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-slate-500 to-slate-700" />
            <div className="px-7 py-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-bold text-gray-900 flex items-center gap-2">
                  <Columns3 className="h-5 w-5 text-slate-600" />
                  {t('Add Column','Kolon Ekle')}
                </h2>
                <button onClick={() => setColumnModal(false)} className="h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>

              {/* Shape selector */}
              <div className="space-y-1.5 mb-4">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('Column Shape','Kolon Şekli')}</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { val: 'daire', label: t('Circle','Daire'), Icon: Circle },
                    { val: 'kare', label: t('Square','Kare'), Icon: Square },
                    { val: 'dikdortgen', label: t('Rectangle','Dikdörtgen'), Icon: RectangleHorizontal },
                  ].map(({ val, label, Icon }) => (
                    <button key={val} onClick={() => setColForm(f => ({ ...f, shape: val }))}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-2xl border-2 transition-all ${
                        colForm.shape === val ? 'border-slate-600 bg-slate-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                      <Icon className={`h-6 w-6 ${colForm.shape === val ? 'text-slate-700' : 'text-gray-400'}`} />
                      <span className={`text-xs font-medium ${colForm.shape === val ? 'text-slate-700' : 'text-gray-500'}`}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Dimensions */}
              <div className="space-y-3 mb-6">
                {colForm.shape === 'daire' ? (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-600">{t('Diameter (meters)','Çap (metre)')}</label>
                    <input type="number" min="0.5" max="10" step="0.5" value={colForm.d}
                      onChange={e => setColForm(f => ({ ...f, d: parseFloat(e.target.value) || 1 }))}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" />
                  </div>
                ) : colForm.shape === 'kare' ? (
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-600">{t('Side (meters)','Kenar (metre)')}</label>
                    <input type="number" min="0.5" max="10" step="0.5" value={colForm.w}
                      onChange={e => setColForm(f => ({ ...f, w: parseFloat(e.target.value) || 1, h: parseFloat(e.target.value) || 1 }))}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-600">{t('Width (meters)','En (metre)')}</label>
                      <input type="number" min="0.5" max="20" step="0.5" value={colForm.w}
                        onChange={e => setColForm(f => ({ ...f, w: parseFloat(e.target.value) || 1 }))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-600">{t('Height (meters)','Boy (metre)')}</label>
                      <input type="number" min="0.5" max="20" step="0.5" value={colForm.h}
                        onChange={e => setColForm(f => ({ ...f, h: parseFloat(e.target.value) || 1 }))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button onClick={() => setColumnModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 font-medium">
                  {t('Cancel','İptal')}
                </button>
                <button data-testid="column-add-ok" onClick={addColumn}
                  className="flex-1 py-2.5 bg-slate-700 hover:bg-slate-800 text-white rounded-xl text-sm font-semibold transition-colors">
                  {t('OK — Add Column','OK — Kolon Ekle')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HallEditorPage;